// Чат-виджет: пузырь → окно чата в Shadow DOM. Отправка сообщений, поллинг
// ответов ассистента («печатает…» пока ждём), форма заявки, состояния
// loading / offline / limit. MVP-решение — поллинг с интервалом;
// ActionCable — v-next. Эмоции аватара — волна 3: только статичная картинка.
import {
  ApiError,
  AssistantCard,
  CartItem,
  ChatAction,
  ChatBlock,
  ChatFile,
  ChatMessage,
  ContactInfo,
  FormFields,
  FormPatch,
  GalleryCard,
  PaymentCard,
  RosterCard,
  ShopCard,
  ShopConfig,
  VisitorContext,
  WidgetApi,
  WidgetConfig,
} from './api';
import { type CartItemRequest, loadShopAdapter, type ShopAdapter } from './adapters';
import {
  cartItems,
  cartPlan,
  type CartState,
  cartStateWith,
  cartStorageKey,
  checkoutData,
  needsCartSync,
  orderCartFields,
  readCartState,
  writeCartState,
} from './cart';
import { hasPaidMarker } from './ads';
import { backoffDelayMs } from './backoff';
import { holdMs } from './reveal';
import { Composer } from './composer.ts';
import { Poller } from './poller.ts';
import { Lightbox } from './lightbox';
import { isRenderableBlock, pickSwitch } from './blocks';
import { filterLeadName, formatRuPhone, LeadFieldKind, leadFieldErrorKey } from './contact';
import { autoOpenDelayMs } from './delay';
import { galleryCardLabel, galleryCards, splitPrice } from './gallery';
import { linkify } from './linkify';
import { watchLauncherAnchor } from './launcher_anchor';
import { createMetrikaReporter, marksOnTopicAnswer, shouldTrackVisitorReply, watchClientId } from './metrika';
import { type NavigationContext, opensInFrame, opensInPlace } from './navigation';
import { bubbleGapMs, burstMs, readDelayMs, restMs, tailMs, typingDelayMs, typingHeadMs } from './pacing';
import { onServiceWorkerOpen, subscribeToPush } from './push';
import { seenReportId } from './receipt';
import { cartBody, cartFields, csrfField, needsRemoteToken, shopCards } from './shop';
import { WIDGET_CSS } from './styles';
import { linkAsksForChat } from './talk_link';
import {
  firstTypeStep,
  longestPhrase,
  nextTypeStep,
  teaserDismissal,
  teaserPhrases,
  TypeStep,
} from './teaser';
import { resolveWidgetTheme } from './theme';
import { createTimers, TimerHandle } from './timers';

// Строки до загрузки конфига (сам конфиг привозит тексты из локали сервера).
const FALLBACK_TEXTS: Record<string, string> = {
  open: 'Открыть чат',
  close: 'Свернуть чат',
  loading: 'Загрузка…',
  offline: 'Нет соединения. Проверьте интернет и попробуйте ещё раз.',
  retry: 'Повторить',
};

// Каданс поллинга согласован с per-IP лимитом 20 req/min (rack_attack.rb):
// даже минута непрерывного «печатает…» — это 15 GET + запас на POST/config.
// Насколько близко к низу ленты гость считается «читающим последнее»: ближе —
// «печатает…» можно подкрутить к нему, дальше — он листает историю, и дёргать
// прокрутку нельзя.
const NEAR_BOTTOM_PX = 80;
const POLL_INTERVAL_MS = 8000;
const POLL_INTERVAL_AWAITING_MS = 4000;
// Свёрнутый чат / неактивная вкладка: медленный фоновый поллинг ради бейджа и
// звука — только когда диалог уже начат (иначе не поллим вовсе).
const POLL_INTERVAL_BACKGROUND_MS = 30_000;
const AWAIT_REPLY_TIMEOUT_MS = 90_000;
const MAX_MESSAGE_LENGTH = 2000; // = Channels::Widget::PostMessage::MAX_CONTENT_LENGTH
// Вложения — зеркало серверных лимитов (Message::MAX_FILES / MAX_FILE_SIZE /
// ACCEPTED_FILE_TYPES): клиентская проверка до отправки, сервер валидирует сам.
const MAX_FILES = 5;
const MAX_FILE_SIZE = 15 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'application/zip',
];
// Пауза до автооткрытия — настройка владельца (конфиг, секунды); дефолт и
// границы держит autoOpenDelayMs (./delay).
// Тизер-приглашение у лаунчера (виджет-паритет, волна 2): бабл с приглашением
// всплывает, если окно не открыли за это время — позже первого впечатления, но
// до того, как гость уйдёт. Крестик гасит приглашение на две недели (срок и
// разбор метки — teaserDismissal в ./teaser), открытие чата — до конца визита.
// Тумблера владельца НЕТ намеренно — тизер вкл по умолчанию, dismissible;
// тумблер можно добавить позже, не раздувая настройки.
const TEASER_DELAY_MS = 10_000;
// Сколько помним платный клик (`yclid` на посадочной): 30 дней — окно
// атрибуции Директа и Метрики, то же, что у куки магазина.
const AD_CLICK_TTL_MS = 30 * 24 * 60 * 60 * 1000;
// Повторная попытка автооткрытия, когда посетитель занят формой сайта.
const AUTO_OPEN_RETRY_MS = 15_000;
// Гость печатает на странице — автооткрытие ждёт. Одного фокуса мало: у поля
// адреса всплывает список подсказок, фокус уходит на него, и «поле в фокусе»
// становится ложью ровно в тот момент, когда мешать нельзя больше всего
// (39плит.рф, 31.07.2026 — панель накрыла подсказки адреса). Считаем гостя
// занятым ещё столько после последнего нажатия клавиши.
const HOST_TYPING_IDLE_MS = 20_000;
// Presence «гость на сайте / ушёл» (виджет-паритет, волна 2): heartbeat раз в
// ~45с, пока вкладка видима и диалог начат; кабинет по свежести отметки рисует
// «на сайте» (окно 90с) / «был N назад». MIN_GAP гасит строчку ре-стуков по
// visibilitychange. Никаких PII/текста — только visitor_key (§9). Каданс ниже
// per-widget троттла presence (AntiAbuse::WidgetLimits::PRESENCE_PER_MINUTE).
const PRESENCE_BEAT_MS = 45_000;
const PRESENCE_MIN_GAP_MS = 30_000;
// «Динь»: не чаще раза в 1.2 с (пачка ответов не строчит), пропущенный до
// первого жеста звук доигрывается, если ему меньше минуты.
const DING_THROTTLE_MS = 1200;
const DING_PENDING_TTL_MS = 60_000;
// Человеческий темп ответа — паузы «на прочтение», циклы «печатает…»,
// осадка между пузырями — живёт в ./pacing (чистый модуль, тестируется
// юнит-тестами): здесь только вызовы с Math.random().
const SVG_NS = 'http://www.w3.org/2000/svg';
// Композер растёт с текстом (autosize) до ~4 строк, дальше — внутренний скролл.
// Совпадает с CSS .composer textarea max-height (styles.ts) — порт marketpult.
const COMPOSER_MAX_HEIGHT = 110;

// Гейт цели site_chat_reply переехал в metrika.ts (marksOnTopicAnswer +
// shouldTrackVisitorReply): там он тестируется юнит-тестами, а здесь жил в
// DOM-классе, куда node --test не заглядывает. Заодно исправлен смысл — цель
// отмечает реплику ПОСЕТИТЕЛЯ после профильного ответа, а не сам ответ бота.

/** Заголовок вкладки с бейджем непрочитанных: «(N) База» при N>0, иначе — сама
 *  база. Чистая функция — тестируема в изоляции (порт marketpult renderTabBadge). */
export function tabTitle(base: string, unseen: number): string {
  return unseen > 0 ? `(${unseen}) ${base}` : base;
}

// Живая валидация формы контакта (виджет-паритет, волна 2) переехала в
// contact.ts: правила номера и почты обязаны совпадать с серверными, а чистый
// модуль без DOM тестируется юнит-тестами (contact.test.mjs).
//
// Ре-показ формы после перезагрузки: свежая просьба «оставьте контакт» поднимает
// форму снова (порт CONTACT_REVIVE_MS marketpult).
const CONTACT_REVIVE_MS = 30 * 60 * 1000;

declare global {
  interface Window {
    /** Старый Safari — префиксный AudioContext. */
    webkitAudioContext?: typeof AudioContext;
  }
}

/** Сколько ждём первый кадр видео-аватара, прежде чем показать статичный:
 *  запас на медленную сеть, но не настолько долгий, чтобы гость успел заметить
 *  пустой квадрат в шапке. */
const AVATAR_VIDEO_TIMEOUT_MS = 2500;

export interface WidgetOptions {
  /** public_key виджета — неймспейс localStorage-метки автооткрытия. */
  publicKey?: string;
  /** Текст из data-prefill embed-тега — предзаполняет пустое поле ввода. */
  prefill?: string;
  /** data-launcher="none": страница открывает чат СВОЕЙ кнопкой через
   *  window.assiistent.open() — пузыря, тизеров и автооткрытия нет (чат
   *  поддержки в кабинете). По умолчанию — обычный пузырь. */
  launcher?: 'bubble' | 'none';
}

export class ChatWidget {
  private config: WidgetConfig | null = null;
  private configPromise: Promise<void> | null = null;
  /** Виджет выключен: сервер сообщил, что этот посетитель заблокирован (§12).
   *  DOM снесён, таймеры сняты, обратного пути в этой загрузке нет — см. disable(). */
  private disabled = false;
  /** Все отложенные вызовы виджета: один stop() гасит и поллинг, и автооткрытие,
   *  и тизер, и presence-heartbeat, и паузы раскрытия (./timers). */
  private readonly timers = createTimers();

  /** Когда гость последний раз печатал на САМОЙ странице (не в виджете). */
  private lastHostTypingAt = 0;
  private lastId = 0;
  /** public_key коллеги, которому уже передали разговор (§8) — второй такой же
   *  блок в ленте (перечитывание истории, повторный поллинг) ничего не делает. */
  private switchedKey = '';
  /** Кто вёл разговор до прихода коллеги (§8): имя для подписи и ключ, по
   *  которому лента вернётся назад. Без пути назад один вопрос про платформу
   *  навсегда уводил гостя от бота, ради которого он на сайт и пришёл. */
  private previousAgent: { key: string; name: string } | null = null;
  private renderedIds = new Set<number>();
  /** Последовательная отрисовка ленты: пока многопузырьковый ответ раскрывается
   *  с паузами, следующее сообщение ждёт своей очереди — порядок цел. */
  private revealChain: Promise<void> = Promise.resolve();
  /** false до первой выборки: та отдаёт ИСТОРИЮ, её рисуем мгновенно; пейсинг
   *  пузырей включается только для ответов, пришедших в живую ленту. */
  private liveFeed = false;
  /** Поколение раскрытия: send() открывает новое, и всё, что запланировано в
   *  старом, дорисовывается мгновенно (посетитель вмешался — спектакль отменён). */
  private revealEpoch = 0;
  /** Прерыватели пауз раскрытия: settleReveal() дёргает их, чтобы хвост пузырей
   *  лёг на экран сразу, не дожидаясь таймеров «печатает…». */
  private sleepBreakers: Array<() => void> = [];
  private awaitingReplyUntil = 0;
  /** Read-таймер: откладывает «печатает…» на паузу «прочтения» после отправки —
   *  индикатор не вспыхивает синхронно с POST (порт marketpult 66e46ef). */
  private readTimer: TimerHandle | null = null;
  /** Таймер цикла набора (startTypingCycle): «печатает…» горит пачками —
   *  burst → пауза → burst, — а не непрерывно всё время генерации ответа. */
  private typingCycleTimer: TimerHandle | null = null;
  /** Горел ли дотс в момент прихода ответа. Если ответ пришёл в паузе цикла
   *  (или спонтанно — оператор), renderContent даёт короткий добор набора
   *  перед первым пузырём, чтобы сообщение не выпадало из ниоткуда. */
  private typingSeenAtArrival = true;
  /** Текст последней реплики посетителя — его длина задаёт read-паузу (readDelayMs). */
  private lastVisitorText = '';
  /** Цикл опроса ленты: каданс, защита от наложения и правило «пустой виджет
   *  на чужой странице публичный API не трогает» — в poller.ts. */
  private readonly poller = new Poller(
    {
      active: () => this.opened || this.lastId !== 0,
      delay: () => this.pollDelay(),
      tick: () => this.poll(),
    },
    this.timers,
  );
  /** Неудачные опросы ПОДРЯД — каданс поллинга растягивается backoffDelayMs,
   *  чтобы виджет не долбил лежащий API (план §8). Первый успех сбрасывает. */
  private pollFailures = 0;
  private sending = false;
  private limitReached = false;
  private opened = false;
  private trackedOpen = false;
  // Бот уже дал профильный ответ — предпосылка цели site_chat_reply. Живёт в
  // памяти вкладки: цель и так уходит один раз на посетителя (localStorage
  // внутри emitTrack), а после перезагрузки посетитель всё равно сначала
  // получит новый ответ, прежде чем ответить на него.
  private botAnsweredOnTopic = false;
  // Посетитель написал, но целевой ли разговор — ещё неизвестно: класс приходит
  // вместе с ОТВЕТОМ бота. Поэтому site_chat_message_sent не уходит в момент
  // отправки, а ждёт ответа и уходит, только если тот не признан оффтопом.
  private pendingMessageSent = false;
  private unseen = 0;
  /** База заголовка вкладки (title хоста без нашего «(N) »-префикса) и последнее
   *  выставленное нами значение: вместе возвращают ЖИВОЙ заголовок хоста, даже
   *  если хост менял его сам (SPA), пока висел бейдж непрочитанных (renderTabBadge). */
  private baseTitle: string | null = null;
  private lastAppliedTitle: string | null = null;
  private autoOpened = false;
  /** Гость открыл чат САМ (клик по пузырю или по приглашению). Автооткрытие —
   *  не в счёт: в холодном демо чат раскрывается через секунду сам, и до
   *  11.09.2026 это навсегда глушило приглашение у закрытого пузыря —
   *  «у сотрудников с демо нет приветствий в закрытом состоянии» (владелец). */
  private openedByUser = false;
  private autoOpenSpentFallback = false;
  /** Авто-переоткрытие при непрочитанном ответе — один раз за сессию (не долбим
   *  каждый ответ/возврат, уважаем закрытие гостём). */
  private replyAutoOpenSpent = false;
  /** Тизер-приглашение у лаунчера (виджет-паритет, волна 2). */
  private teaser: HTMLDivElement | null = null;
  /** Подписчики на подсказки бота для формы сайта (window.assiistent.on).
   *  Массив, а не один колбэк: страница вправе слушать из нескольких мест. */
  private readonly patchListeners: ((patch: FormPatch) => void)[] = [];
  /** id реплик, чьи подсказки уже отданы странице: поллинг перечитывает ленту,
   *  и повторный вызов колбэка переписал бы форму, которую человек уже правил. */
  private readonly patchedIds = new Set<number>();
  /** Адаптер движка магазина (doc/shop_adapters.md) — догружается один раз,
   *  только когда владелец выбрал движок в кабинете. null — ещё не звали. */
  private shopAdapter: Promise<ShopAdapter | null> | null = null;
  /** Контакты, названные в разговоре (MessagesResponse.contact): ими
   *  заполняется форма заказа САЙТА на странице оформления. */
  private contact: ContactInfo | null = null;
  /** Последний подтверждённый состав (message.cart) — пометка о нём уезжает
   *  в комментарий формы заказа, чтобы менеджер видел, что заказ из чата. */
  private lastCart: CartItem[] = [];
  /** Форму заказа заполняем один раз за загрузку страницы: поллинг
   *  перечитывает ленту, а второй проход переписал бы поля, которые человек
   *  уже правит. */
  private checkoutFilled = false;
  /** Что уже положили (id сообщения + состав) — в памяти вкладки на случай
   *  недоступного localStorage (приватный режим). */
  private cartMemory: CartState | null = null;
  /** Сообщения, чью корзину кладёт ЭТА вкладка: решение принято на приходе
   *  (appendServerMessage), а не на отрисовке — иначе две вкладки успевали
   *  обе решить «ещё не клали». */
  private readonly cartFreshIds = new Set<number>();
  /** «Мы на странице оформления заказа сайта?» — считается один раз. */
  private checkoutPage: Promise<boolean> | null = null;

  /** Открытый просмотрщик фото: карточки ленты, текущий индекс и узлы, чтобы
   *  стрелки и клавиатура листали без пересборки DOM. */
  /** Лайтбокс галереи (Lightbox) — создаётся при первом открытии, когда
   *  корень виджета уже смонтирован. */
  private lightboxView: Lightbox | null = null;

  /** Поля формы САЙТА, объявленные страницей (window.assiistent.context).
   *  Едут с КАЖДОЙ репликой: посетитель правит форму по ходу разговора, и
   *  сервер должен видеть последнее состояние, а не первое. */
  private formContext: FormFields = [];
  /** Чипы «Продолжить в Telegram/MAX»: держим ссылку, чтобы после каждой дописки
   *  возвращать их в конец ленты (keepContinueChipsLast). */
  private continueChips: HTMLDivElement | null = null;
  /** Печатающиеся фразы приглашения (настройка сотрудника): строка на экране,
   *  сам список, текущий шаг печати и его таймер. Строка null — печати нет
   *  (одна фраза, prefers-reduced-motion или тизер уже снят). */
  private teaserLine: HTMLSpanElement | null = null;
  private teaserTyping: string[] = [];
  private teaserStep: TypeStep = firstTypeStep();
  private teaserTimer: TimerHandle | null = null;
  /** Welcome-back: метка «вернувшийся гость» уже записана за эту загрузку. */
  private returningMarked = false;
  /** Presence heartbeat: время последнего стука (MIN_GAP-троттл ре-стуков). */
  private lastBeatAt = 0;
  private pendingPrefill = '';
  /** Непрочитанные пузыри посетителя (id + галочки) — ждут списка seen_ids
   *  поллинга, чтобы подняться до ✓✓ (read-receipt, виджет-паритет, волна 2). */
  private unseenTicks: Array<{ id: number; ticks: HTMLSpanElement }> = [];
  /** Обратный read-receipt: id последней реплики бизнеса, УЖЕ ЛЕЖАЩЕЙ в ленте
   *  (не «пришедшей», а отрисованной), и курсор, уже принятый сервером. */
  private renderedBusinessId = 0;
  private reportedSeenId = 0;
  private audioCtx: AudioContext | null = null;
  private lastDingAt = 0;
  private pendingDingAt = 0;
  private pushAsked = false;
  /** Автодетект Метрики хоста: цели воронки (site_chat_*) летят в счётчик
   *  страницы. Каждая цель — один раз на посетителя, отметка переживает
   *  перезагрузку (localStorage по publicKey), поэтому создаём отправитель в
   *  конструкторе: полю-инициализатору options ещё не видны. */
  private readonly reportMetrika: (action: string) => void;
  /** ClientID счётчика хозяина страницы: ключ офлайн-конверсии по заявке. */
  private metrikaClientId = '';
  /** Номер того же счётчика: сервер запоминает его у сотрудника, чтобы клиенту
   *  не пришлось искать номер руками (Metrika::Counter). */
  private metrikaCounterId = 0;

  private shadow!: ShadowRoot;
  /** Хост Shadow DOM в body страницы: disable() выносит его целиком — вместе с
   *  панелью и пузырём (виджета на странице не остаётся). */
  private host!: HTMLDivElement;
  private root!: HTMLDivElement;
  private themeObserver?: MutationObserver;
  private stopLauncherAnchor?: () => void;
  private bubbleButton!: HTMLButtonElement;
  /** Плашка «подключился человек» уже показана для текущей серии его реплик. */
  private humanAnnounced = false;
  private badge!: HTMLSpanElement;
  private panel!: HTMLDivElement;
  private headerBox!: HTMLDivElement;
  private bodyBox!: HTMLDivElement;
  private messagesBox!: HTMLDivElement;
  private typingBubble!: HTMLDivElement;
  private banner!: HTMLDivElement;
  private bannerText!: HTMLSpanElement;
  private bannerRetry!: HTMLButtonElement;
  private input!: HTMLTextAreaElement;
  /** Кадр, в котором пересчитается высота поля (0 — не запрошен), и замеры
   *  прошлого пересчёта: набор внутри строки высоту не меняет, и трогать
   *  вёрстку на каждое нажатие незачем (см. ./autosize). */
  /** Крестик очистки поля ввода: виден только при непустом вводе (composer.ts). */
  private clearButton!: HTMLButtonElement;
  private sendButton!: HTMLButtonElement;
  /** Поведение поля ввода: Enter, autosize, крестик очистки (composer.ts). */
  private composer!: Composer;
  private chatView!: HTMLDivElement;
  /** Инлайн-форма контакта в ленте чата (виджет-паритет, волна 2): открытая
   *  форма, флаг «контакт оставлен» и последняя CTA — чат остаётся под формой. */
  private leadForm: HTMLFormElement | null = null;
  /** Контакты, которые бот уже собрал в этом разговоре (message.lead_prefill):
   *  ими предзаполняется форма заявки. Живут в поле, а не в аргументе, потому
   *  что форму строит appendLeadCta по последнему ответу с просьбой контактов. */
  private leadPrefill: Partial<Record<LeadFieldKind, string>> | null = null;
  private leadSubmitted = false;
  private lastLeadCta: HTMLButtonElement | null = null;
  /** Выбранные, но ещё не отправленные вложения (чипы над композером). */
  private pendingFiles: File[] = [];
  private fileInput!: HTMLInputElement;
  private filesRow!: HTMLDivElement;

  constructor(
    private readonly api: WidgetApi,
    private readonly visitorKey: string,
    private readonly options: WidgetOptions = {},
  ) {
    this.pendingPrefill = options.prefill ?? '';
    this.reportMetrika = createMetrikaReporter(window, options.publicKey ?? '');
    // Спрашиваем ClientID сразу и переспрашиваем, пока счётчик не ответит:
    // сниппет Метрики бывает НИЖЕ нашего тега или подключается отложенно
    // (после согласия на куки, из тег-менеджера) — тогда единственный вопрос
    // в момент старта не дал бы ничего никогда. Без ключа заявка уедет без
    // офлайн-конверсии (Leads::Conversion), а молчун не уйдёт целью
    // (Conversations::SilentVisitor). Опрос живёт в реестре таймеров, поэтому
    // stop() у заблокированного посетителя гасит и его.
    watchClientId(
      window,
      (delay, fn) => void this.timers.after(delay, fn),
      (clientId, counterId) => {
        this.metrikaClientId = clientId;
        this.metrikaCounterId = counterId;
      },
      // Номер счётчика — сразу, как только он найден на странице: без ClientID
      // (куки-баннер, блокировщик) он всё равно нужен серверу, иначе карточка
      // сотрудника остаётся без счётчика и офлайн-конверсию потом слать некуда.
      { onCounter: (counterId) => void (this.metrikaCounterId = counterId) },
    );
  }

  /** Где висит пузырь. В стилях зашито `right: 20px; bottom: 20px`, и клиент,
   *  у которого виджет накрыл кнопку прокрутки, поправить это не мог: правило
   *  живёт внутри shadow DOM, куда стили страницы не достают (MKEL, 04.09.2026).
   *  Теперь положение приходит настройкой карточки сотрудника.
   *
   *  Сторону меняем ОБОИМИ свойствами: оставить `right` от стилей и добавить
   *  `left` значило бы растянуть пузырь на всю ширину окна. Боковой отступ
   *  ставим всегда, даже нулевой: ноль — законное значение настройки (пузырь
   *  вплотную к краю), и отбрасывать его как «пусто» нельзя. */
  private placeRoot(): void {
    const root = this.root;
    if (!root) return;

    const bottom = this.config?.offset_bottom;
    if (typeof bottom === 'number' && bottom > 0) {
      root.style.bottom = `${bottom}px`;
      // Окну чата — чтобы не уехать шапкой за верх экрана (.panel в styles.ts).
      root.style.setProperty('--w-offset-bottom', `${bottom}px`);
    }

    const side = typeof this.config?.offset_side === 'number' ? `${this.config.offset_side}px` : null;
    if (this.config?.side === 'left') {
      root.style.right = 'auto';
      root.style.left = side ?? '20px';
    } else if (side) {
      root.style.right = side;
    }
  }

  mount(): void {
    const host = document.createElement('div');
    this.host = host;
    host.setAttribute('data-assiistent-widget', '');
    this.shadow = host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = WIDGET_CSS;
    this.shadow.appendChild(style);
    this.watchHostTyping();

    const root = this.el('div', 'root');
    this.root = root;
    // Вернувшийся нарушитель: пузырь не должен мигнуть до ответа сервера —
    // при подсказке о прошлой блокировке рисуем скрытым, и reveal() покажет
    // виджет, только если конфиг подтвердит, что блока больше нет.
    if (this.blockedHint()) root.classList.add('hidden');
    this.panel = this.el('div', 'panel hidden');
    this.bubbleButton = this.el('button', 'bubble');
    this.bubbleButton.type = 'button';
    // Иконка — отдельным узлом: она спокойно «дышит» прозрачностью (CSS,
    // не таймер), как индикатор «на связи», а бейдж непрочитанных на той же
    // кнопке остаётся неподвижным. Под prefers-reduced-motion движения нет (§5).
    this.bubbleButton.appendChild(this.el('span', 'bubble-icon', '💬'));
    this.bubbleButton.setAttribute('aria-label', this.t('open'));
    this.bubbleButton.addEventListener('click', () => this.toggle());
    this.badge = this.el('span', 'badge hidden');
    this.badge.setAttribute('aria-hidden', 'true');
    this.bubbleButton.appendChild(this.badge);

    // Без пузыря (data-launcher="none"): кнопка есть, но спрятана навсегда —
    // open()/close() ей не управляют, чат живёт от кнопки хост-страницы.
    if (this.noLauncher()) this.bubbleButton.classList.add('hidden');

    root.append(this.panel, this.bubbleButton);
    this.shadow.appendChild(root);
    document.body.appendChild(host);
    this.applyHostTheme();
    this.watchHostTheme();
    // WebAudio разрешён только после первого жеста посетителя (autoplay-политика)
    // — разблокируем заранее, чтобы «динь» не терялся (порт из marketpult).
    ['pointerdown', 'keydown', 'touchend'].forEach((type) =>
      document.addEventListener(type, this.unlockAudio, { once: true, passive: true }),
    );
    document.addEventListener('visibilitychange', () => this.onVisibilityChange());
    // Клик по Web Push нотификации сфокусировал уже открытую вкладку —
    // сервис-воркер просит открыть чат (public/widget-sw.js).
    onServiceWorkerOpen(() => this.openWith());
    // Состояние посетителя спрашиваем у сервера СРАЗУ, не дожидаясь клика по
    // пузырю: заблокированному (§12) виджет не должен показаться и после F5, а
    // без этого запроса пузырь висел бы до первого клика — вернувшемуся гостю
    // конфиг не тянут ни автооткрытие («потрачено»), ни закрытый тизер. Запрос
    // общий (configPromise), лишнего похода в API не будет.
    void this.ensureConfig();
    void this.openFromTalkLink();
    // Переход по ссылке из чата в этом окне (консультант на своём сайте) —
    // чат открывается на новой странице сам.
    this.openAfterNavigation();
    // Приглашения — только у пузыря: без него чат открывает сама страница.
    if (!this.noLauncher()) {
      void this.scheduleAutoOpen();
      this.scheduleTeaser();
    }
    this.startPresence();
  }

  private noLauncher(): boolean {
    return this.options.launcher === 'none';
  }

  /** Пришёл по кнопке «Поговорить» с нашей главной — раскрываем чат сразу.
   *
   *  Ссылка блока «уже работают» ведёт на САЙТ клиента: соцдоказательство — это
   *  живой сайт, а не демо на нашем домене. Но человек нажал «Поговорить», а не
   *  «посмотреть сайт», и оставить его искать пузырь в углу чужой страницы
   *  значит потерять ровно то действие, ради которого он пришёл.
   *
   *  Конфиг ждём, как автооткрытие: заблокированному гостю (§12) виджет
   *  раскрываться не должен, а до ответа сервера `disabled` ещё не выставлен.
   *
   *  Адрес страницы НЕ правим: она чужая, а счётчик клиента к этому моменту
   *  просмотр уже отправил — чистка адреса его отчёту всё равно не поможет.
   *
   *  Целью «гость открыл чат сам» это не считается: клик был на НАШЕЙ главной,
   *  и приписывать его счётчику клиента — врать в его отчёте. `openWith` гасит
   *  приглашение до конца визита (гость и так уже в чате) и метку не ставит. */
  private async openFromTalkLink(): Promise<void> {
    if (!linkAsksForChat(window.location.search)) return;
    if (!(await this.ensureConfig())) return;
    if (this.opened) return; // успел открыть сам, пока грузился конфиг

    this.openWith();
  }

  // Тему виджета диктует САЙТ, а не операционка гостя: настройка владельца →
  // <html data-theme> хоста → яркость фона страницы → светлая (resolveWidgetTheme).
  // Системная prefers-color-scheme в цепочке не участвует намеренно — из-за неё
  // гость с тёмной ОС видел тёмный виджет на светлом сайте (дефект). Класс
  // .w-theme-light — маркер выбора: светлая палитра и так дефолт .root.
  private applyHostTheme(): void {
    const theme = resolveWidgetTheme({
      preference: this.config?.theme,
      hostTheme: document.documentElement.getAttribute('data-theme'),
      backgrounds: this.hostBackgrounds(),
    });
    this.root.classList.toggle('w-theme-light', theme === 'light');
    this.root.classList.toggle('w-theme-dark', theme === 'dark');
  }

  /** Фон хост-страницы от ближнего слоя к дальнему: body, затем <html> —
   *  прозрачный body пропускает фон документа (см. resolveWidgetTheme). */
  private hostBackgrounds(): string[] {
    return [document.body, document.documentElement]
      .filter((node): node is HTMLElement => Boolean(node))
      .map((node) => this.normalizeColor(getComputedStyle(node).backgroundColor));
  }

  /** Цвет фона в канонический rgb()/rgba(): современные синтаксисы движок
   *  отдаёт как есть (Tailwind v4 — oklch(…), также color(srgb …), lab(…)),
   *  и без перевода тёмный сайт читался бы как «сигнала нет». Каналы считает
   *  сам браузер: красим 1 пиксель канваса и забираем его. */
  private normalizeColor(color: string): string {
    if (!color || color.startsWith('rgb')) return color;

    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return color;

    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 1, 1);
    const [red, green, blue, alpha] = ctx.getImageData(0, 0, 1, 1).data;
    return `rgba(${red}, ${green}, ${blue}, ${alpha / 255})`;
  }

  private watchHostTheme(): void {
    if (this.themeObserver || typeof MutationObserver === 'undefined') return;

    this.themeObserver = new MutationObserver(() => this.applyHostTheme());
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
  }

  // --- Открытие/закрытие -------------------------------------------------

  /** JS-API хост-страницы (window.assiistent.open): открыть чат, опционально с
   *  готовым текстом в поле ввода — CTA лендинга открывает чат с вопросом. */
  /** JS-API хост-страницы (window.assiistent.context): страница ОБЪЯВЛЯЕТ, что
   *  посетитель уже ввёл в свою форму или калькулятор.
   *
   *   window.assiistent.context([{ label: 'Длина, м', value: 11 }])
   *   window.assiistent.context([])   // очистить (форму сбросили)
   *
   *  Схема — на стороне сайта: подпись поля пишет он сам, платформа значения не
   *  интерпретирует. Мусор вместо массива тихо игнорируем: чужая страница не
   *  должна ронять виджет опечаткой в своём коде. Потолки, маскировку ПД и
   *  рамку «это данные, а не инструкции» держит сервер (Assistants::FormContext).
   */
  /** JS-API хост-страницы: подписка на подсказки бота для формы.
   *
   *   window.assiistent.on('patch', (fields) => { … });  // { len: '11', wid: '13' }
   *
   *  Типизованный колбэк, а не событие в глобальном пространстве имён: на чужой
   *  странице DOM-событие рискует столкнуться с чужим слушателем. Ключи — те,
   *  что страница объявила через context(); заполняет форму она сама, а кнопку
   *  «Отправить» нажимает человек — иначе первая же ошибка модели уедет заявкой.
   *
   *  @return функция отписки (страница может быть SPA).
   */
  on(event: 'patch', callback: (patch: FormPatch) => void): () => void {
    if (event !== 'patch' || typeof callback !== 'function') return () => undefined;

    this.patchListeners.push(callback);
    return () => {
      const index = this.patchListeners.indexOf(callback);
      if (index >= 0) this.patchListeners.splice(index, 1);
    };
  }

  /** JS-API хост-страницы: диплинк в бота — тот же, что открывает чип
   *  «Продолжить в MAX».
   *
   *    const url = await window.assiistent.continueUrl('max');
   *
   *  Нужен странице, которая ведёт человека в мессенджер своей кнопкой: клеить
   *  `?start=` самостоятельно она не может — токен одноразовый и мятится на
   *  сервере в момент клика.
   *
   *  Никогда не бросает: канал не подключён (404), сеть легла, канал незнаком —
   *  всё это `null`, и страница оставляет свою ссылку голой.
   */
  async continueUrl(kind: string): Promise<string | null> {
    if (kind !== 'max' && kind !== 'telegram') return null;

    try {
      const { url } = await this.api.fetchContinueLink(kind, this.visitorKey, this.metrikaClientId);
      return url || null;
    } catch {
      return null;
    }
  }

  setContext(fields: FormFields): void {
    if (Array.isArray(fields)) {
      this.formContext = fields.filter((field) => field && field.value != null);
      return;
    }
    // Плоский объект — документированная форма вызова: context({'Длина, м': 11}).
    // Всё остальное (строка, null, число) считаем «контекста нет».
    this.formContext = fields && typeof fields === 'object' ? { ...fields } : [];
  }

  openWith(prefill?: string): void {
    if (prefill) this.pendingPrefill = prefill;
    this.autoOpened = false;
    // Кнопка на странице и тап по пуш-уведомлению — такое же вовлечение гостя,
    // как клик по пузырю: приглашение молчит до конца визита (замечание Codex).
    // Метку ставим здесь, а не флагом byUser в open(), чтобы не пере-означить
    // цель Метрики «гость открыл чат сам» задним числом.
    this.hideTeaserForVisit();
    if (this.opened) {
      this.applyPrefill();
      (this.input as HTMLTextAreaElement | undefined)?.focus();
      return;
    }
    this.open();
  }

  private toggle(): void {
    if (this.opened) {
      this.close(true);
    } else {
      this.open(true);
    }
  }

  /** @param byUser открытие по действию гостя (клик по пузырю или по тизеру),
   *    а не автооткрытием по тумблеру владельца и не авто-переоткрытием ответа.
   *    Различаем в аналитике: клик — намерение гостя, автооткрытие — наша
   *    инициатива, и мерить их одной целью значит не увидеть разницы. */
  private open(byUser = false): void {
    if (this.disabled) return;

    this.opened = true;
    // Гость открыл чат сам — приглашение не нужно до конца визита. Автооткрытие и
    // авто-переоткрытие ответа — НАША инициатива: приглашение гаснет только на
    // этой странице (trackedOpen), а на следующей гость его снова увидит.
    if (byUser) {
      this.openedByUser = true;
      this.hideTeaserForVisit();
    } else {
      this.hideTeaser();
    }
    if (byUser) this.emitTrack('opened-by-user');
    if (!this.trackedOpen) {
      this.trackedOpen = true;
      this.emitTrack('opened');
    }
    this.clearUnseen();
    this.panel.classList.remove('hidden');
    this.root?.classList.add('w-open');
    // Чат открывается «на месте кнопки»: пузырь прячем, закрытие — крестиком в
    // шапке панели. Иначе на экране одновременно и кнопка, и открытый чат.
    this.bubbleButton.classList.add('hidden');
    this.bubbleButton.setAttribute('aria-label', this.t('close'));
    if (this.config) {
      this.startPolling();
      this.applyPrefill();
      // Prefill мог проставиться, пока панель была скрыта (scrollHeight=0) —
      // перемеряем autosize теперь, когда поле видимо (порт marketpult open()).
      this.autosizeInput();
      // Автооткрытие не крадёт клавиатуру у читающего посетителя (marketpult):
      // фокус в композер — только при осознанном открытии.
      if (!this.autoOpened) this.input.focus();
      return;
    }
    void this.loadConfig();
  }

  /** @param byUser гость свернул чат сам — крестиком или тапом по пузырю.
   *    Автозакрытий у нас нет, но `close()` зовётся и из disable() (блокировка
   *    §12), а это не действие гостя и целью быть не должно. */
  private close(byUser = false): void {
    if (byUser) this.emitTrack('closed-by-user');
    this.opened = false;
    this.autoOpened = false;
    this.panel.classList.add('hidden');
    this.root?.classList.remove('w-open');
    if (!this.noLauncher()) this.bubbleButton.classList.remove('hidden');
    this.bubbleButton.setAttribute('aria-label', this.t('open'));
    // Сам закрыл чат — автооткрытие больше не навязываем никогда.
    this.spendAutoOpen();
    // …но приглашение у пузыря гость ещё не видел, если чат открылся сам:
    // заводим его заново от момента закрытия.
    if (!this.openedByUser) this.scheduleTeaser();
    if (this.lastId > 0) {
      // Диалог начат: фоновый поллинг ловит ответ для бейджа/звука. Каданс
      // считает pollDelay: ждём ответ — быстрый, иначе — медленный фоновый.
      this.schedulePoll(this.pollDelay());
    } else {
      this.stopPolling();
    }
  }

  // --- Блокировка посетителя (spec §12) ------------------------------------

  /** Сервер сообщил, что этот посетитель заблокирован: виджета для него больше
   *  нет. Панель сворачивается, пузырь уходит из DOM вместе с хостом, все
   *  таймеры снимаются разом (./timers) — заблокированный перестаёт и поллить,
   *  и стучать presence, то есть не жжёт наш API дальше.
   *
   *  МОЛЧА: ни текста, ни причины, ни кода ошибки — обратная связь помогала бы
   *  подбирать обход правил (§12). Виджета просто нет.
   *
   *  Необратимо в пределах загрузки страницы: конфиг обнуляем, поэтому poll() и
   *  автооткрытие выходят по !config даже из уже запущенных промисов. Снятый
   *  владельцем блок вернёт виджет на следующей загрузке — состояние приезжает
   *  с сервера, а не из localStorage. */
  private disable(): void {
    if (this.disabled) return;

    this.disabled = true;
    this.timers.stop();
    this.poller.stop();
    this.readTimer = null;
    this.typingCycleTimer = null;
    this.config = null;
    this.opened = false;
    this.themeObserver?.disconnect();
    this.stopLauncherAnchor?.();
    this.hideTeaser();
    this.host?.remove();
    this.rememberBlocked(true);
  }

  /** Подсказка «этому посетителю виджет уже сносили». НЕ источник правды: её
   *  чистит любая уборка localStorage, и решает всё равно сервер. Нужна ровно
   *  для одного — не мигать пузырём на следующей загрузке, пока конфиг в пути. */
  private blockedHint(): boolean {
    try {
      return window.localStorage.getItem(this.blockedKey()) === '1';
    } catch {
      return false;
    }
  }

  private rememberBlocked(blocked: boolean): void {
    try {
      if (blocked) window.localStorage.setItem(this.blockedKey(), '1');
      else window.localStorage.removeItem(this.blockedKey());
    } catch {
      // приватный режим — подсказка живёт до перезагрузки страницы
    }
  }

  private blockedKey(): string {
    return `assiistent_blocked_${this.options.publicKey ?? ''}`;
  }

  /** Пришёл ли посетитель по платному клику. У магазина от этого зависит цена
   *  товара (рекламная доля сидит в цене каждого шара, но платит её только тот,
   *  кого привела реклама), и бот обязан называть ту же цифру, что человек
   *  видит на карточке рядом.
   *
   *  Признаки — метки, которые рекламные системы Яндекса сами дописывают к
   *  посадочной ссылке: `yclid` (Директ), `ybaip` (Яндекс Бизнес — автоматичка,
   *  yclid она НЕ ставит) и разметка кампании `utm_medium=cpc`. Реферер для
   *  этого не годится — с рекламы Яндекс приходит как yandex.ru, ровно как и с
   *  обычной выдачи, и вся органика попала бы в платные.
   *
   *  Одного yclid мало: 17.08.2026 посетитель пришёл из Директа на
   *  «ассиистент.рф/?ybaip=1», рекламным не признался, и в диалоге не осталось
   *  следа платного клика.
   *
   *  Метка живёт только в первой ссылке, поэтому помним её 30 дней — окно
   *  атрибуции Директа, то же, что у куки магазина: вернувшийся напрямую
   *  человек всё равно считается рекламным.
   */
  private fromPaidClick(): boolean {
    const key = `assiistent_ad_${this.options.publicKey ?? ''}`;
    try {
      if (hasPaidMarker(window.location.search)) {
        window.localStorage.setItem(key, String(Date.now()));
        return true;
      }
      const seen = Number(window.localStorage.getItem(key) ?? 0);
      return seen > 0 && Date.now() - seen < AD_CLICK_TTL_MS;
    } catch {
      return false; // приватный режим: без метки считаем гостя своим
    }
  }

  /** Сервер подтвердил, что посетитель НЕ заблокирован: показываем виджет и
   *  снимаем подсказку — разбаненный получает пузырь сразу, а следующая
   *  загрузка уже ничего не прячет. */
  private reveal(): void {
    this.root.classList.remove('hidden');
    this.rememberBlocked(false);
  }

  // --- Автооткрытие (тумблер владельца, один раз на посетителя) ------------

  /** Паузу до автооткрытия задаёт владелец (конфиг, секунды), поэтому конфиг
   *  тянем тихо сразу — но отсчёт ведём от старта виджета: медленная сеть не
   *  должна сдвигать приглашение позже, чем просил владелец. Гость, которому
   *  автооткрытие уже показывали, не стоит и запроса — выходим до конфига. */
  private async scheduleAutoOpen(): Promise<void> {
    // На телефоне панель разворачивается на ВЕСЬ экран и накрывает то, ради
    // чего человек пришёл: карточку товара с кнопкой «Купить». На компьютере
    // она занимает угол. Поэтому автооткрытие — только на компьютерах; на
    // телефоне остаётся пузырь-приглашение (решение владельца 10.09.2026).
    if (this.detectDevice() === 'mobile') return;

    // Метку «уже открывали» проверяем ПОСЛЕ конфига: у демо холодной кампании
    // она не действует, а знать об этом можно только из конфига.
    const startedAt = Date.now();
    if (!(await this.ensureConfig()) || !this.config?.auto_open) return;
    if (this.autoOpenSpent()) return;

    const left = autoOpenDelayMs(this.config.auto_open_delay) - (Date.now() - startedAt);
    this.timers.after(Math.max(0, left), () => void this.autoOpen());
  }

  /** Автораскрытие по истечении паузы: решение принимает тумблер auto_open
   *  владельца. Сработавшее автооткрытие помечается в localStorage — один раз
   *  на посетителя (аналог INVITE_KEY marketpult). */
  private async autoOpen(): Promise<void> {
    if (this.opened || this.autoOpenSpent()) return;
    if (!(await this.ensureConfig()) || !this.config?.auto_open) return;
    if (this.opened || this.autoOpenSpent()) return; // открыл/закрыл сам, пока грузился конфиг
    // Человек заполняет форму САЙТА — не лезем поверх. На телефоне панель
    // разворачивается на весь экран и накрывает поле вместе с подсказками
    // адреса: посетитель на 39плит.рф печатал адрес, чат открылся сам и закрыл
    // выпадающий список (31.07.2026). Ждём, пока освободится — попытку не
    // сжигаем, отложенное автооткрытие сработает, когда человек закончит.
    if (this.hostBusy()) {
      this.timers.after(AUTO_OPEN_RETRY_MS, () => void this.autoOpen());
      return;
    }
    this.spendAutoOpen();
    this.autoOpened = true;
    this.open();
  }

  /** Гость занят страницей: печатает прямо сейчас или печатал только что.
   *  Второе важнее первого — у поля адреса всплывает список подсказок, фокус
   *  уходит на него, и проверка фокуса перестаёт защищать. */
  private hostBusy(): boolean {
    return this.hostFieldFocused() || Date.now() - this.lastHostTypingAt < HOST_TYPING_IDLE_MS;
  }

  /** Слушаем ввод на странице хоста: события из Shadow DOM ретаргетятся на сам
   *  элемент виджета — по нему и отличаем свои нажатия от чужих. */
  private watchHostTyping(): void {
    const seen = (event: Event): void => {
      if (event.target === this.host) return; // печатают в самом виджете

      this.lastHostTypingAt = Date.now();
    };
    document.addEventListener('keydown', seen, true);
    document.addEventListener('input', seen, true);
  }

  /** Фокус на поле хост-страницы: человек печатает — вводит адрес, размеры,
   *  контакты. Свои поля виджета не считаем: они внутри Shadow DOM, и в
   *  document.activeElement виден только сам хост-элемент виджета. */
  private hostFieldFocused(): boolean {
    const active = document.activeElement as HTMLElement | null;
    if (!active || active === this.host) return false;

    const tag = active.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active.isContentEditable === true;
  }

  private autoOpenSpent(): boolean {
    // Демо холодной кампании раскрывается каждый заход: страница ровно про
    // этот разговор, и закрытый чат делает её бессмысленной.
    if (this.config?.auto_open_always) return false;

    try {
      return window.localStorage.getItem(this.autoOpenKey()) === '1';
    } catch {
      return this.autoOpenSpentFallback;
    }
  }

  private spendAutoOpen(): void {
    this.autoOpenSpentFallback = true;
    try {
      window.localStorage.setItem(this.autoOpenKey(), '1');
    } catch {
      // приватный режим — метка живёт до перезагрузки страницы
    }
  }

  private autoOpenKey(): string {
    return `assiistent_auto_open_${this.options.publicKey ?? ''}`;
  }

  // --- Тизер-приглашение у лаунчера (виджет-паритет, волна 2) ---------------

  private scheduleTeaser(): void {
    if (this.teaserDismissed()) return;
    this.timers.after(TEASER_DELAY_MS, () => void this.showTeaser());
  }

  /** Показать бабл-приглашение у лаунчера, если гость не открыл чат сам. Текст —
   *  из серверной локали, поэтому тихо тянем конфиг (общий промис с автооткрытием,
   *  повторно не грузит; оффлайн → тизера просто нет). Не конфликтуем с
   *  автооткрытием: открытый/открывающийся чат тизер не показывает. */
  private async showTeaser(): Promise<void> {
    // openedByUser — гость открывал чат САМ (и, возможно, закрыл до срока
    // тизера): он уже вовлечён, приглашать незачем (замечание Codex P3).
    // Автооткрытие сюда не считается: чат раскрыла НАША инициатива, и закрывший
    // его гость приглашение ещё не видел.
    if (this.disabled || this.opened || this.openedByUser || this.teaser || this.teaserDismissed()) return;
    if (!(await this.ensureConfig())) return;
    // disabled — конфиг принёс блокировку, пока мы его ждали: приглашать некого.
    if (this.disabled || this.opened || this.openedByUser || this.teaser || this.teaserDismissed()) return;
    this.teaser = this.buildTeaser();
    // Фазу вешаем ПОСЛЕ сборки: buildTeaser зовёт fillTeaser, когда this.teaser
    // ещё не присвоен — вешать было бы не на что.
    this.applyTeaserPhase();
    this.root.appendChild(this.teaser);
  }

  private buildTeaser(): HTMLDivElement {
    const teaser = this.el('div', 'teaser');
    // Клик по тексту = открыть чат; тизер гасим этому посетителю (уже вовлекли).
    const text = this.el('button', 'teaser-text');
    text.type = 'button';
    this.fillTeaser(text);
    // Приглашение гасит само открытие (open → hideTeaserForVisit) — отказом тап
    // по нему не считается.
    text.addEventListener('click', () => this.open(true));
    const close = this.el('button', 'teaser-close', '✕');
    close.type = 'button';
    close.setAttribute('aria-label', this.t('teaser_dismiss'));
    close.title = this.t('teaser_dismiss');
    close.addEventListener('click', (event) => {
      event.stopPropagation();
      this.dismissTeaser();
    });
    teaser.append(text, close);
    return teaser;
  }

  /** Текст приглашения: одна фраза статично или несколько — печатью по кругу.
   *  Статично — и когда фраз меньше двух (дефолт: клиент, который ничего не
   *  настраивал, не получает бегущую строку на своём сайте), и при
   *  prefers-reduced-motion (§5): гость видит целую фразу, без движения. */
  private fillTeaser(text: HTMLButtonElement): void {
    const phrases = teaserPhrases(this.config?.teaser_phrases);
    if (phrases.length < 2 || this.prefersReducedMotion()) {
      text.textContent = phrases[0] ?? this.t('teaser');
      return;
    }
    // Печать меняет видимую строку по букве, поэтому доступное имя кнопки
    // держим неподвижным приглашением — скринридер не читает обрывки.
    text.setAttribute('aria-label', this.t('teaser'));
    // Невидимый близнец самой длинной фразы держит высоту пузыря: без него
    // тизер прыгал бы на каждой букве и «разъезжался» на длинной фразе.
    const sizer = this.el('span', 'teaser-sizer', longestPhrase(phrases));
    sizer.setAttribute('aria-hidden', 'true');
    this.teaserLine = this.el('span', 'teaser-line', phrases[0]);
    const stack = this.el('span', 'teaser-stack');
    stack.append(sizer, this.teaserLine);
    text.appendChild(stack);
    this.teaserTyping = phrases;
    this.teaserStep = firstTypeStep();
    // Первая фраза не выкладывается готовой — цикл начинается с пустой паузы,
    // и её набор посетитель увидит.
    this.teaserLine.textContent = '';
    this.syncTeaserTyping();
  }

  /** Один шаг печати и заявка на следующий — через общий реестр таймеров
   *  (своих setTimeout в виджете нет: stop() обязан гасить и печать). */
  private typeTeaser(): void {
    this.teaserTimer = null;
    if (!this.teaserLine) return;

    this.teaserStep = nextTypeStep(this.teaserStep, this.teaserTyping);
    this.teaserLine.textContent = this.teaserTyping[this.teaserStep.index].slice(0, this.teaserStep.typed);
    this.applyTeaserPhase();
    this.syncTeaserTyping();
  }

  /** Фаза цикла — на сам пузырь: гашение на паузе и моргание с затуханием
   *  рисует CSS (styles.ts), скрипт только объявляет, что сейчас происходит.
   *
   *  Атрибут появляется ТОЛЬКО у печатающего тизера: статичному (одна фраза
   *  или prefers-reduced-motion) фаза `idle` погасила бы пузырь навсегда. */
  private applyTeaserPhase(): void {
    if (!this.teaser || !this.teaserLine) return;

    this.teaser.setAttribute('data-phase', this.teaserStep.phase);
  }

  /** Вхолостую не тикаем: печать идёт, только пока строка на экране, виджет жив
   *  и вкладку видно. Скрытая вкладка — пауза на текущем шаге, возврат
   *  (onVisibilityChange) продолжает с того же места. */
  private syncTeaserTyping(): void {
    const awake = this.teaserLine !== null && !this.disabled && document.visibilityState !== 'hidden';
    if (awake && this.teaserTimer === null) {
      this.teaserTimer = this.timers.after(this.teaserStep.delay, () => this.typeTeaser());
    } else if (!awake && this.teaserTimer !== null) {
      this.timers.clear(this.teaserTimer);
      this.teaserTimer = null;
    }
  }

  private hideTeaser(): void {
    this.teaser?.remove();
    this.teaser = null;
    this.teaserLine = null;
    this.syncTeaserTyping(); // строки на экране нет — печать останавливается
  }

  /** Отказ от приглашения — КРЕСТИК, и на срок, а не навсегда: через две недели
   *  (teaserDismissal) гость фактически новый посетитель. Храним время отказа.
   *
   *  Тап по самому приглашению сюда БОЛЬШЕ НЕ ХОДИТ: им открывают чат, и на
   *  телефоне это единственный способ его открыть — одно открытие глушило фразы
   *  на всё устройство. Вовлечение гасит приглашение на визит
   *  (hideTeaserForVisit), отказ — только крестик. */
  private dismissTeaser(): void {
    this.hideTeaser();
    try {
      window.localStorage.setItem(this.teaserKey(), String(Date.now()));
    } catch {
      // приватный режим — метка живёт до перезагрузки страницы
    }
  }

  /** Гость ОТКРЫЛ чат сам — приглашать больше незачем, но это вовлечение, а не
   *  отказ. Метка в sessionStorage: живёт до конца визита, в том числе при
   *  переходах по страницам сайта (одного `trackedOpen` хватало лишь на текущую
   *  страницу), и умирает вместе со вкладкой — в следующий визит приглашение
   *  вернётся. */
  private hideTeaserForVisit(): void {
    this.hideTeaser();
    try {
      window.sessionStorage.setItem(this.teaserKey(), '1');
    } catch {
      // приватный режим — метка живёт до перезагрузки страницы
    }
  }

  private teaserHiddenForVisit(): boolean {
    try {
      return window.sessionStorage.getItem(this.teaserKey()) !== null;
    } catch {
      return false;
    }
  }

  /** Молчать ли с приглашением: гашение на визит (открывали чат) или отказ
   *  крестиком, ещё не истёкший. Метку-мусор и метку старого бессрочного формата
   *  стираем на месте — иначе она глушила бы приглашение навсегда. */
  private teaserDismissed(): boolean {
    if (this.teaserHiddenForVisit()) return true;
    try {
      const state = teaserDismissal(window.localStorage.getItem(this.teaserKey()), Date.now());
      if (state === 'stale') window.localStorage.removeItem(this.teaserKey());
      return state === 'active';
    } catch {
      return false;
    }
  }

  private teaserKey(): string {
    return `assiistent_teaser_dismissed_${this.options.publicKey ?? ''}`;
  }

  // --- Загрузка конфига (loading / ошибка загрузки) ----------------------

  private async loadConfig(): Promise<void> {
    this.panel.replaceChildren(this.el('div', 'loading', this.t('loading')));
    const loaded = await this.ensureConfig();
    // Конфиг принёс блокировку (§12): виджет уже снёс себя — ни ленты, ни
    // «оффлайн»-баннера, показывать нечего и некому.
    if (this.disabled) return;
    if (loaded) {
      this.startPolling();
      this.input.focus();
      return;
    }
    const retry = this.el('button', 'btn secondary', this.t('retry'));
    retry.type = 'button';
    retry.addEventListener('click', () => void this.loadConfig());
    const box = this.el('div', 'loading');
    box.append(this.el('span', '', this.t('offline')), retry);
    this.panel.replaceChildren(box);
  }

  /** Конфиг + постройка интерфейса ровно один раз: параллельные вызовы
   *  (автооткрытие и клик по пузырю) делят один запрос. */
  private async ensureConfig(): Promise<boolean> {
    if (this.disabled) return false;

    try {
      this.configPromise ??= this.fetchConfigAndBuild();
      await this.configPromise;
      return true;
    } catch (error) {
      this.configPromise = null;
      if (error instanceof ApiError && error.status === 404) {
        this.disable();
        return false;
      }
      // Сервер не ответил (оффлайн): состояние неизвестно — виджет работает как
      // раньше, а подсказку блокировки не трогаем, правду скажет следующий ответ.
      this.root.classList.remove('hidden');
      return false;
    }
  }

  private async fetchConfigAndBuild(): Promise<void> {
    const config = await this.api.fetchConfig(this.visitorKey);
    // Посетитель заблокирован (§12): конфига в таком ответе и нет — сервер
    // отдаёт один флаг, а виджет молча исчезает вместе с пузырём. Строить
    // нечего. Старый сервер поля не шлёт — ветка не срабатывает никогда.
    if (config.blocked) {
      this.disable();
      return;
    }

    this.config = config;
    this.reveal();
    // Настройка темы владельца приезжает с конфигом — до него виджет живёт на
    // авто-определении по сайту; перекрашиваем, как только узнали выбор.
    this.applyHostTheme();
    // Положение — оттуда же и по той же причине: на mount() конфига ещё нет,
    // и пузырь встал бы на место из стилей (поймано на живом сайте клиента
    // 04.09.2026: настройку сохранили, а виджет не сдвинулся).
    this.placeRoot();
    this.stopLauncherAnchor?.();
    this.stopLauncherAnchor = watchLauncherAnchor(this.root, config.launcher_anchor, () => this.placeRoot());
    this.buildChrome();
    // Страница оформления заказа сайта — ленту тянем, даже если чат свёрнут.
    this.pollIfCheckout();
  }

  // --- Построение интерфейса ---------------------------------------------

  private buildChrome(): void {
    this.buildHeader();
    this.buildChatView();
    this.bodyBox = this.el('div', 'body');
    this.bodyBox.style.cssText = 'display:flex;flex-direction:column;flex:1;min-height:0;';
    this.bodyBox.append(this.chatView);
    this.panel.replaceChildren(this.headerBox, this.bodyBox);
    this.appendGreeting();
    this.applyPrefill();
  }

  /** Prefill в поле ввода (embed data-prefill или аргумент openWith) — только
   *  в пустой композер: набранный посетителем текст не затираем (marketpult). */
  private applyPrefill(): void {
    const input = this.input as HTMLTextAreaElement | undefined;
    if (!this.pendingPrefill || !input || input.value) return;
    input.value = this.pendingPrefill;
    this.pendingPrefill = '';
    this.autosizeInput();
    if (this.opened && !this.autoOpened) input.focus();
  }

  private buildHeader(): void {
    const assistant = this.config!.assistant;
    this.headerBox = this.el('div', 'header');
    this.headerBox.append(this.buildIdentity(assistant), this.buildClose());
  }

  /** Аватар+имя: ссылка на того же бота (config.header_bot_url) — кому удобнее в
   *  мессенджере, уходит туда одним тапом; без URL это обычный контейнер (не ссылка). */
  private buildIdentity(assistant: AssistantCard): HTMLElement {
    const url = this.config!.header_bot_url;
    const identity = url ? this.el('a', 'identity') : this.el('div', 'identity');
    if (url) {
      const link = identity as HTMLAnchorElement;
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.title = this.t('header_link');
    }
    identity.append(this.buildAvatarWrap(assistant), this.buildWho(assistant));
    return identity;
  }

  /** Аватар в обёртке с пульсирующей точкой «онлайн»; точка декоративна —
   *  доступность несёт подзаголовок, поэтому aria-hidden (§5: без движения при
   *  prefers-reduced-motion — гасит CSS). */
  private buildAvatarWrap(assistant: AssistantCard): HTMLDivElement {
    const wrap = this.el('div', 'avatar-wrap');
    const dot = this.el('span', 'avatar-dot');
    dot.setAttribute('aria-hidden', 'true');
    wrap.append(this.buildAvatar(assistant), dot);
    return wrap;
  }

  /** Имя + роль + подзаголовок «Обычно отвечает за пару минут» (тексты из локали
   *  сервера; подзаголовок читаем напрямую — не показываем ключ, если он отсутствует). */
  private buildWho(assistant: AssistantCard): HTMLDivElement {
    const who = this.el('div', 'who');
    who.append(this.el('div', 'name', assistant.name), this.el('div', 'role', assistant.role));
    const subtitle = this.config!.texts.subtitle;
    if (subtitle) who.appendChild(this.el('div', 'subtitle', subtitle));
    return who;
  }

  /** Кнопка «вернуться к прежнему сотруднику» — в ШАПКЕ, а не в ленте: чистка
   *  устаревших контролов (dropStaleControls) выносит из ленты всё, кроме чипов
   *  «продолжить в мессенджере», да и уехала бы она вверх с третьей репликой. */
  private buildBack(): HTMLButtonElement | null {
    const previous = this.previousAgent;
    if (!previous) return null;

    const label = this.t('switch_back').replace('%{name}', previous.name);
    const button = this.el('button', 'back', '←');
    button.type = 'button';
    button.title = label;
    button.setAttribute('aria-label', label);
    button.addEventListener('click', () => void this.switchBack());
    return button;
  }

  private buildClose(): HTMLButtonElement {
    const closeButton = this.el('button', 'close', '✕');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', this.t('close'));
    closeButton.addEventListener('click', () => this.close(true));
    return closeButton;
  }

  /** Аватар шапки: видео-луп (если задан) → статичная картинка → буква-фолбэк.
   *  prefers-reduced-motion (§5): видео не проигрываем — остаётся первый кадр. */
  private buildAvatar(assistant: AssistantCard): HTMLElement {
    if (assistant.avatar_video_url) {
      const video = document.createElement('video');
      this.guardAvatarVideo(video, assistant);
      video.className = 'avatar';
      video.src = assistant.avatar_video_url;
      // Постер из avatar_url НЕ ставим: это другой кроп (крупный план головы),
      // а ролик — «голова+плечи», отсюда скачок «мелкая картинка → крупнее видео».
      // preload='auto' показывает СОБСТВЕННЫЙ первый кадр ролика → неподвижный
      // кадр совпадает с проигрыванием, оживление без смены кадрирования (§5).
      video.preload = 'auto';
      video.muted = true;
      video.setAttribute('muted', ''); // атрибут (не только свойство) — надёжный autoplay
      video.loop = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('aria-label', assistant.name);
      if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) video.autoplay = true;
      return video;
    }
    return this.buildStillAvatar(assistant);
  }

  /** Статичный аватар: картинка, а без неё — буква имени. */
  private buildStillAvatar(assistant: AssistantCard): HTMLElement {
    if (assistant.avatar_url) {
      const img = document.createElement('img');
      img.className = 'avatar';
      img.src = assistant.avatar_url;
      img.alt = assistant.name;
      // Картинка тоже может не доехать (CSP чужого сайта, протухшая подпись
      // вложения) — тогда вместо пустого квадрата рисуем букву имени.
      img.addEventListener('error', () => {
        if (img.isConnected) img.replaceWith(this.el('div', 'avatar-fallback', assistant.name.charAt(0)));
      });
      return img;
    }
    return this.el('div', 'avatar-fallback', assistant.name.charAt(0));
  }

  /** Ролик может не доехать на ЧУЖОМ сайте — и молча: строгий CSP хоста
   *  (`media-src`), прокси или блокировщик оставляют <video> в readyState 0 без
   *  события error. В шапке тогда пустой квадрат вместо лица. Проверено на
   *  39плит.рф 31.07.2026: mp4 с нашего домена там не загружается вовсе.
   *  Поэтому ждём первый кадр и, не дождавшись, показываем статичный аватар. */
  private guardAvatarVideo(video: HTMLVideoElement, assistant: AssistantCard): void {
    const fallback = (): void => {
      // HAVE_CURRENT_DATA (2), а не «> 0»: в энергосбережении iOS отдаёт
      // readyState 1 — метаданные есть, кадра нет, и шапка оставалась пустой
      // (жалоба владельца 16.08.2026, Барсик на телефоне).
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA || !video.isConnected) return;

      video.replaceWith(this.buildStillAvatar(assistant));
    };
    video.addEventListener('error', fallback);
    this.timers.after(AVATAR_VIDEO_TIMEOUT_MS, fallback);
  }

  private buildChatView(): void {
    this.chatView = this.el('div', '');
    this.chatView.style.cssText = 'display:flex;flex-direction:column;flex:1;min-height:0;';

    this.messagesBox = this.el('div', 'messages');
    this.typingBubble = this.el('div', 'typing hidden');
    this.typingBubble.append(
      this.el('span', 'dot'),
      this.el('span', 'dot'),
      this.el('span', 'dot'),
      this.el('span', '', this.t('typing')),
    );

    this.banner = this.el('div', 'banner hidden');
    this.bannerText = this.el('span', '');
    this.bannerRetry = this.el('button', '', this.t('retry'));
    this.bannerRetry.type = 'button';
    this.bannerRetry.addEventListener('click', () => {
      this.hideBanner();
      void this.poll();
    });
    this.banner.append(this.bannerText, this.bannerRetry);

    const composer = this.el('div', 'composer');
    this.input = document.createElement('textarea');
    this.input.placeholder = this.t('input_placeholder');
    this.input.maxLength = MAX_MESSAGE_LENGTH;
    this.input.rows = 1;
    this.sendButton = this.el('button', 'btn', this.t('send'));
    this.sendButton.type = 'button';
    this.sendButton.addEventListener('click', () => void this.sendMessage());
    composer.append(this.buildAttach(), this.buildInputWrap(), this.sendButton);
    // Поведение поля (Enter отправляет, рост под текст, крестик очистки) —
    // в composer.ts; крестик рождается в buildInputWrap выше.
    this.composer = new Composer(this.input, this.clearButton, {
      send: () => void this.sendMessage(),
      maxHeight: COMPOSER_MAX_HEIGHT,
    });
    this.composer.listen();

    // Чипы выбранных файлов — между лентой и композером (пусто → скрыто).
    this.filesRow = this.el('div', 'files hidden');
    this.chatView.append(this.messagesBox, this.banner, this.filesRow, composer);
    const branding = this.buildBranding();
    if (branding) this.chatView.appendChild(branding);
  }

  /** Подпись «Работает на ассИИстент» у бесплатного сотрудника (§8): ссылка с
   *  UTM и мини-логотип под полем ввода. Ненавязчиво: мелко, приглушённо и вне
   *  ленты — переписку не загораживает и с прокруткой не уезжает. Платный
   *  тариф подписи не получает (сервер не присылает branding). */
  private buildBranding(): HTMLElement | null {
    const branding = this.config!.branding;
    if (!branding?.url || !branding.text) return null;

    const wrap = this.el('div', 'branding');
    const link = this.el('a', 'branding-link');
    link.href = branding.url;
    link.target = '_blank';
    link.rel = 'noopener';
    if (branding.logo_url) {
      const logo = document.createElement('img');
      logo.src = branding.logo_url;
      logo.alt = '';
      logo.setAttribute('aria-hidden', 'true');
      link.appendChild(logo);
    }
    link.appendChild(document.createTextNode(branding.text));
    wrap.appendChild(link);
    return wrap;
  }

  /** Поле ввода + крестик очистки в общей обёртке (порт marketpult
   *  _widget.html.erb:179-191): крестик виден при непустом поле, тап стирает всё
   *  набранное — прежде всего prefill (выделять его на телефоне мучительно). */
  private buildInputWrap(): HTMLSpanElement {
    this.clearButton = this.el('button', 'input-clear hidden', '✕');
    this.clearButton.type = 'button';
    this.clearButton.setAttribute('aria-label', this.t('input_clear'));
    this.clearButton.title = this.t('input_clear');
    this.clearButton.addEventListener('click', () => this.clearInput());
    const wrap = this.el('span', 'input-wrap');
    wrap.append(this.input, this.clearButton);
    return wrap;
  }

  private autosizeInput(): void {
    this.composer.resize();
  }

  private clearInput(): void {
    this.composer.clear();
  }

  // --- Вложения посетителя (порт site_chat из marketpult) -------------------

  /** Скрепка композера: НАСТОЯЩАЯ кнопка (доступна с клавиатуры — Tab +
   *  Enter/Space, замечание Codex-ревью) открывает скрытый file-input с
   *  accept по вайтлисту сервера. */
  private buildAttach(): HTMLSpanElement {
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.multiple = true;
    this.fileInput.accept = ACCEPTED_FILE_TYPES.join(',');
    this.fileInput.hidden = true;
    this.fileInput.tabIndex = -1;
    this.fileInput.addEventListener('change', () => this.filesPicked());
    const attach = this.el('button', 'attach', '📎');
    attach.type = 'button';
    attach.setAttribute('aria-label', this.t('attach'));
    attach.title = this.t('attach');
    attach.addEventListener('click', () => this.fileInput.click());
    const wrap = this.el('span', 'attach-wrap');
    wrap.append(attach, this.fileInput);
    return wrap;
  }

  /** Отбор выбранного: тип по вайтлисту, размер ≤ 15 МБ, всего ≤ 5 — мимо
   *  лимита файл не попадает в чипы, причина показывается баннером. */
  private filesPicked(): void {
    const picked = Array.from(this.fileInput.files ?? []);
    this.fileInput.value = ''; // повторный выбор того же файла снова даёт change
    for (const file of picked) {
      if (this.pendingFiles.length >= MAX_FILES) {
        this.showBanner(this.t('file_too_many'), false);
        break;
      }
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        this.showBanner(this.t('file_type'), false);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        this.showBanner(this.t('file_too_large'), false);
        continue;
      }
      this.pendingFiles.push(file);
    }
    this.renderFileChips();
  }

  private renderFileChips(): void {
    this.filesRow.replaceChildren();
    this.filesRow.classList.toggle('hidden', this.pendingFiles.length === 0);
    this.pendingFiles.forEach((file, index) => {
      const chip = this.el('span', 'file-chip');
      chip.append(
        this.el('span', 'file-chip-name', file.name),
        this.el('span', 'file-chip-size', this.formatSize(file.size)),
      );
      const remove = this.el('button', 'file-chip-remove', '✕');
      remove.type = 'button';
      remove.setAttribute('aria-label', this.t('file_remove'));
      remove.title = this.t('file_remove');
      remove.addEventListener('click', () => {
        this.pendingFiles.splice(index, 1);
        this.renderFileChips();
      });
      chip.appendChild(remove);
      this.filesRow.appendChild(chip);
    });
  }

  /** «1,4 МБ» / «120 КБ» — человекочитаемый размер, единицы из локали сервера. */
  private formatSize(bytes: number): string {
    const mb = 1024 * 1024;
    if (bytes >= mb) return `${(bytes / mb).toFixed(1).replace('.', ',')} ${this.t('unit_mb')}`;
    return `${Math.max(1, Math.round(bytes / 1024))} ${this.t('unit_kb')}`;
  }

  /** Вложения серверной реплики в пузырь: картинка — превью-миниатюрой,
   *  остальное — file-link «📎 имя (размер)». */
  private appendServerFiles(bubble: HTMLDivElement, files: ChatFile[]): void {
    const box = this.el('div', 'msg-files');
    files.forEach((file) => box.appendChild(this.fileNode(file)));
    this.insertIntoBubble(bubble, box);
  }

  /** Ссылка на вложение (подписанный blob-URL сервера) — через
   *  textContent/атрибуты, innerHTML виджет не трогает. */
  private fileNode(file: ChatFile): HTMLAnchorElement {
    const image = file.content_type.startsWith('image/');
    const link = this.el('a', image ? 'msg-image' : 'file-link');
    link.href = file.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    if (image) {
      const img = document.createElement('img');
      img.src = file.url;
      img.alt = file.name;
      img.loading = 'lazy';
      link.appendChild(img);
    } else {
      link.textContent = `📎 ${file.name} (${this.formatSize(file.size)})`;
    }
    return link;
  }

  /** Локальное эхо вложений в пузыре посетителя — строки «📎 имя (размер)»
   *  до ответа сервера; send() заменит их ссылками из ответа. */
  private appendLocalFiles(bubble: HTMLDivElement, files: File[]): HTMLDivElement {
    const box = this.el('div', 'msg-files');
    files.forEach((file) =>
      box.appendChild(this.el('div', 'file-line', `📎 ${file.name} (${this.formatSize(file.size)})`)),
    );
    this.insertIntoBubble(bubble, box);
    return box;
  }

  /** Вставка в пузырь ПЕРЕД галочками доставки — «✓/✓✓» остаются последними. */
  private insertIntoBubble(bubble: HTMLDivElement, node: HTMLElement): void {
    const ticks = bubble.querySelector('.ticks');
    if (ticks) {
      bubble.insertBefore(node, ticks);
    } else {
      bubble.appendChild(node);
    }
  }

  /** Кнопка «Оставить заявку» под сообщением бота — показывается, только когда
   *  бот сам решил собрать контакты (маркер [[заявка]]), не постоянно. Клик
   *  раскрывает форму ИНЛАЙН в ленте (чат остаётся виден), не заменяя экран. */
  private appendLeadCta(): void {
    if (this.leadAlreadySubmitted()) return; // контакт уже оставлен — второй раз не зовём
    const cta = this.el('button', 'lead-cta', this.t('lead_button'));
    cta.type = 'button';
    cta.addEventListener('click', () => this.openLeadForm(cta));
    this.messagesBox.appendChild(cta);
    this.lastLeadCta = cta;
    this.keepContinueChipsLast();
    this.scrollDown();
  }

  // --- Сообщения -----------------------------------------------------------

  private appendGreeting(): void {
    // Вернувшемуся гостю (есть прошлая история — метка в localStorage) — короткое
    // «С возвращением!» вместо первичного приветствия; первый визит слышит
    // АВТОРСКОЕ assistant.greeting (голос персонажа §3), без генерик-рулетки.
    const greeting = this.isReturningVisitor() ? this.t('welcome_back') : this.config!.assistant.greeting;
    if (greeting) this.appendBubble('assistant', greeting);
    // Стартовые чипы консультанта под приветствием (у клиентских ботов пусто).
    const chips = this.config!.chips;
    if (chips?.length) this.appendActions(chips, 'quick');
    this.appendContinueChips();
  }

  // --- Welcome-back вернувшемуся гостю (виджет-паритет, волна 2) -------------

  /** @return есть ли у гостя прошлая история (метка ставится, когда в диалоге
   *  появляется первое сообщение — markReturning). Приватный режим/нет метки →
   *  трактуем как первый визит (авторское приветствие). */
  private isReturningVisitor(): boolean {
    try {
      return window.localStorage.getItem(this.returningKey()) === '1';
    } catch {
      return false;
    }
  }

  /** Пометить гостя вернувшимся на будущие визиты — при первом же сообщении в
   *  диалоге (своя реплика или подтянутая история). Пишем один раз за загрузку. */
  private markReturning(): void {
    if (this.returningMarked) return;
    this.returningMarked = true;
    try {
      window.localStorage.setItem(this.returningKey(), '1');
    } catch {
      // приватный режим — welcome_back просто не сработает в следующий визит
    }
  }

  private returningKey(): string {
    return `assiistent_returning_${this.options.publicKey ?? ''}`;
  }

  /** Постоянные чипы «Продолжить в Telegram/MAX» (spec §W2b): сервер отдаёт
   *  только подключённые каналы; диплинк с одноразовым токеном запрашивается в
   *  момент клика. Недоступный канал — чип тихо прячем.
   *
   *  Живут ВНИЗУ ленты, а не под приветствием: приглашение продолжить в
   *  мессенджере нужно в любой момент разговора, а под приветствием оно уезжает
   *  за верхнюю кромку с третьей реплики и на телефоне его больше не найти.
   *  Держит их последними keepContinueChipsLast. */
  private appendContinueChips(): void {
    const options = this.config!.continue;
    if (!options?.length) return;
    // Класс `continue` — только чтобы прижать чипы к низу ленты (styles.ts):
    // стартовые чипы консультанта такие же `quick`, но они стоят по месту.
    const wrap = this.el('div', 'quick continue');
    options.forEach((option) => {
      const chip = this.el('button', 'quick-chip', option.label);
      chip.type = 'button';
      chip.addEventListener('click', () => void this.openContinueLink(option.kind, chip));
      wrap.appendChild(chip);
    });
    this.continueChips = wrap;
    this.messagesBox.appendChild(wrap);
  }

  /** Вернуть чипы «Продолжить в …» в конец ленты после любой дописки (пузырь,
   *  форма заявки, блок карточек): appendChild уже вставленного узла его
   *  ПЕРЕНОСИТ, поэтому порядок в DOM совпадает с порядком на экране — читалка
   *  экрана и глаз видят одно и то же (WCAG 1.3.2), в отличие от CSS order. */
  private keepContinueChipsLast(): void {
    if (this.continueChips) this.messagesBox.appendChild(this.continueChips);
  }

  private async openContinueLink(kind: string, chip: HTMLButtonElement): Promise<void> {
    if (chip.disabled) return; // защита от даблклика: один клик — один токен
    chip.disabled = true;
    // Ссылку берём тем же путём, что отдаём хост-странице (continueUrl):
    // разойдись они — кнопка сайта и чип виджета вели бы в разные места.
    const url = await this.continueUrl(kind);
    if (!url) {
      chip.remove(); // канал недоступен (404/сеть) — чип тихо прячем
      return;
    }
    // Без feature 'noopener' (с ним open() всегда возвращает null) — opener
    // рвём вручную. Попап-блокер (клик «протух» за время fetch) → переходим
    // в этой вкладке: посетитель явно попросил мессенджер, уход со страницы
    // хост-сайта — ожидаемый исход.
    const popup = window.open(url, '_blank');
    if (popup) popup.opener = null;
    else window.location.assign(url);
    chip.disabled = false;
  }

  private appendBubble(
    kind: 'visitor' | 'assistant' | 'operator' | 'local',
    text: string,
    seen = false,
  ): HTMLDivElement {
    const bubble = this.el('div', `msg ${kind}`);
    // Адреса в ответе бота и оператора — ссылками (linkify.ts): узлы собираем
    // руками, innerHTML не трогаем. Реплика гостя и служебные плашки — как есть.
    if (kind === 'assistant' || kind === 'operator') this.fillLinked(bubble, text);
    else bubble.textContent = text;
    if (kind === 'visitor') {
      this.lastVisitorText = text; // длина прочитанного → read-пауза перед «печатает…»
      bubble.appendChild(this.buildTicks(seen));
    }
    this.messagesBox.appendChild(bubble);
    this.keepContinueChipsLast();
    this.scrollDown();
    return bubble;
  }

  private appendServerMessage(message: ChatMessage, instant = false): void {
    if (this.renderedIds.has(message.id)) return;
    // Дедуп и курсор двигаем на ПРИХОДЕ (не на отрисовке): поллинг, стрельнувший
    // пока многопузырьковый ответ ещё раскрывается, не перепоставит те же id.
    this.renderedIds.add(message.id);
    this.lastId = Math.max(this.lastId, message.id);
    // Остаток удержания (reveal_in_ms): человеческий темп ответа сервер считает,
    // но не спит в воркере — ответ уже в ленте, а «печатает…» горит до срока.
    // Курсор выше уже сдвинут, поэтому следующий опрос дубля не принесёт.
    const hold = instant ? 0 : holdMs(message.reveal_in_ms);
    if (hold > 0) {
      this.holdMessage(message, instant, hold);
      return;
    }
    this.ingestServerMessage(message, instant);
  }

  /** Удержанные до срока ответы: settleReveal() выкладывает их сразу, чтобы
   *  старый ответ не лёг ПОСЛЕ новой реплики гостя как ответ на неё. */
  private heldMessages: Array<{ handle: TimerHandle | null; message: ChatMessage; instant: boolean }> = [];

  private holdMessage(message: ChatMessage, instant: boolean, hold: number): void {
    const entry = { handle: null as TimerHandle | null, message, instant };
    entry.handle = this.timers.after(hold, () => {
      this.heldMessages = this.heldMessages.filter((pending) => pending !== entry);
      this.ingestServerMessage(message, instant);
    });
    this.heldMessages.push(entry);
  }

  private flushHeldMessages(): void {
    this.heldMessages.splice(0).forEach((entry) => {
      this.timers.clear(entry.handle);
      this.ingestServerMessage(entry.message, entry.instant);
    });
  }

  private ingestServerMessage(message: ChatMessage, instant: boolean): void {
    // Корзину сайта тоже решаем НА ПРИХОДЕ: метку «это сообщение кладём»
    // пишем сразу, чтобы вторая вкладка с той же лентой не положила состав
    // ещё раз; сама отправка идёт в отрисовке (appendCart).
    if (message.cart && needsCartSync(message.id, this.cartState().id)) {
      this.cartFreshIds.add(message.id);
      this.saveCartState({ ...this.cartState(), id: message.id });
    }
    this.markReturning(); // в диалоге есть сообщение → в след. визит гость «вернувшийся»
    if (message.role === 'system') return;
    if (message.role !== 'visitor') {
      // Галочки/бейдж/звук — на КАЖДЫЙ ответ ассистента, сразу по приходе (темп
      // раскрытия пузырей на них не влияет). Профильный ответ ВЗВОДИТ предпосылку
      // цели reply (spec §5): сама цель уйдёт, когда посетитель на него ответит.
      this.replyArrived();
      this.notifyUnseen();
      if (marksOnTopicAnswer(message)) {
        this.botAnsweredOnTopic = true;
        // Отложенная цель «написал в чат»: теперь видно, что разговор целевой.
        if (this.pendingMessageSent) {
          this.pendingMessageSent = false;
          this.emitTrack('message-sent');
        }
      }
    }
    const epoch = this.revealEpoch;
    // Состояние дотса фиксируем НА ПРИХОДЕ: пока цепочка раскрытия дойдёт до
    // этого сообщения, replyArrived следующих сообщений пачки перепишет флаг.
    const typingSeen = message.role === 'visitor' || this.typingSeenAtArrival;
    this.revealChain = this.revealChain
      .then(() => this.renderServerMessage(message, instant, epoch, typingSeen))
      .catch(() => undefined); // сломанная отрисовка не должна заклинить ленту
  }

  private async renderServerMessage(
    message: ChatMessage,
    instant: boolean,
    epoch: number,
    typingSeen: boolean,
  ): Promise<void> {
    // Пустой content возможен у чисто-блочного ответа консультанта — пузырь не
    // рисуем; file-only реплика посетителя рисуется ради вложений.
    if (message.content || message.files?.length) {
      await this.renderContent(message, instant, epoch, typingSeen);
    }
    // Форма заявки и блоки — после ПОСЛЕДНЕГО пузыря: просьба приходит в конце.
    if (message.lead) {
      this.leadPrefill = message.lead_prefill ?? null;
      this.appendLeadCta();
    }
    if (message.blocks?.length) this.appendBlocks(message.blocks);
    // Подтверждённый заказ — в корзину сайта (Orders::Cart): после карточек,
    // рядом с ответом, в котором гость сказал «да».
    if (message.cart) this.appendCart(message);
    await this.applySwitch(message.blocks);
    this.deliverPatch(message);
    // Обратный read-receipt: ответ бизнеса ЦЕЛИКОМ лёг в ленту — только теперь
    // его можно честно отчитать прочитанным (остальные гейты — в poll()).
    if (message.role !== 'visitor') {
      this.renderedBusinessId = Math.max(this.renderedBusinessId, message.id);
    }
  }

  /** Реплика посетителя и одиночный ответ ложатся сразу; СВЕЖИЙ ответ с
   *  пузырями (metadata.bubbles) раскрывается по одному с «печатает…» паузой —
   *  как человек, набирающий несколько коротких сообщений (порт из marketpult).
   *  История (instant) рисуется мгновенно отдельными пузырями — перечитывание
   *  старого диалога не должно ждать спектакль набора. Устаревшая эпоха
   *  (посетитель уже вмешался новой репликой) тоже дорисовывается мгновенно. */
  private async renderContent(
    message: ChatMessage,
    instant: boolean,
    epoch: number,
    typingSeen: boolean,
  ): Promise<void> {
    if (message.role === 'visitor') {
      // История/другая вкладка: реплика посетителя приходит поллингом с флагом
      // seen — рисуем ✓/✓✓ сразу по нему; непрочитанную отслеживаем, чтобы
      // поднять ✓✓ по списку seen_ids следующего поллинга.
      const bubble = this.appendBubble('visitor', message.content, message.seen === true);
      if (message.seen !== true) this.trackTick(message.id, bubble);
      if (message.files?.length) this.appendServerFiles(bubble, message.files);
      return;
    }
    const bubbles = message.bubbles?.length ? message.bubbles : [message.content];
    // Кто говорит: до 17.08.2026 ответ человека выглядел точно как ответ бота,
    // и посетитель не понимал, с кем разговаривает.
    const byHuman = message.role === 'operator';
    if (byHuman) {
      this.announceHuman();
      // Подпись над КАЖДОЙ серией реплик человека: плашка о переходе одна, а
      // перечитывая историю, посетитель должен видеть автора и дальше.
      this.messagesBox.appendChild(this.el('div', 'msg-who', this.t('human_label')));
    } else {
      // Ответил бот — разговор вернулся к нему, и следующее подключение
      // человека снова объявляем.
      this.humanAnnounced = false;
    }
    let lastBubble: HTMLDivElement | null = null;
    for (let i = 0; i < bubbles.length; i += 1) {
      // Первый пузырь при горевшем дотсе — сразу: «печатает…» уже отработал
      // (read-пауза + цикл набора), пока ответ готовился. Если же ответ пришёл
      // в ПАУЗЕ цикла или спонтанно (оператор) — короткий добор: сообщение из
      // ниоткуда выдаёт бота не хуже мгновенного ответа. Перед следующими
      // пузырями — пауза набора (тот же человек продолжает).
      // prefers-reduced-motion пейсинг НЕ выключает (решение владельца
      // 30.07.2026, см. scheduleTypingAfterRead): пауза — такт разговора,
      // а не анимация; движение гасит CSS-медиазапрос.
      const paced = !instant && epoch === this.revealEpoch;
      if (paced && i === 0 && !typingSeen) await this.typeOut(tailMs(Math.random()), epoch);
      if (paced && i > 0) await this.typeOut(typingDelayMs(bubbles[i], this.typingJitter()), epoch);
      lastBubble = this.appendBubble(byHuman ? 'operator' : 'assistant', bubbles[i]);
      if (paced && i < bubbles.length - 1) await this.sleep(bubbleGapMs(Math.random()));
    }
    // Вложения (например, медиа оператора из мессенджера) — под последним пузырём.
    if (message.files?.length && lastBubble) this.appendServerFiles(lastBubble, message.files);
  }

  /** Плашка «к разговору подключился человек» — один раз на серию реплик
   *  оператора. Возвращается бот (следующий ответ ассистента) — флаг сбрасываем,
   *  и при новом подключении человека плашка появится снова. */
  private announceHuman(): void {
    if (this.humanAnnounced) return;
    this.humanAnnounced = true;

    const note = this.el('div', 'handoff-note', this.t('human_joined'));
    this.messagesBox.appendChild(note);
    this.keepContinueChipsLast();
    this.scrollDown();
  }

  /** «Печатает…» на ~ms, затем погасить. Длинный набор рвётся паузой посередине
   *  (typingHeadMs): человек печатает пачками, а не одной ровной очередью.
   *  Устаревшая эпоха (посетитель вмешался) обрывает спектакль сразу после
   *  ближайшего сна — settleReveal не ждёт хвост честных таймеров. */
  private async typeOut(ms: number, epoch: number): Promise<void> {
    const head = typingHeadMs(ms, Math.random());
    this.showTypingBubble();
    await this.sleep(head > 0 ? head : ms);
    // Пауза посреди набора — гасим дотс, НЕ вынимая строку: следом идёт вторая
    // пачка, и лента не должна дважды сложиться и разложиться.
    if (head === 0 || epoch !== this.revealEpoch) return this.hideTypingBubble();

    this.hushTypingBubble();
    await this.sleep(restMs(Math.random()));
    if (epoch !== this.revealEpoch) return this.hideTypingBubble();

    this.showTypingBubble();
    await this.sleep(ms - head);
    this.hideTypingBubble();
  }

  /** Разброс длительности набора 0.75×–1.25×: одинаковые длины пузырей не
   *  должны давать одинаковый такт (выдаёт робота). */
  private typingJitter(): number {
    return 0.75 + Math.random() * 0.5;
  }

  /** Пауза раскрытия, прерываемая settleReveal(): сломанный сон резолвится
   *  сразу, чтобы хвост пузырей не заставлял ждать реплику посетителя. */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const breaker = (): void => {
        this.timers.clear(timer);
        resolve();
      };
      const timer = this.timers.after(ms, () => {
        this.sleepBreakers = this.sleepBreakers.filter((pending) => pending !== breaker);
        resolve();
      });
      this.sleepBreakers.push(breaker);
    });
  }

  /** Посетитель вмешался посреди раскрытия: спектакль набора отменяем — хвост
   *  пузырей ложится мгновенно ДО его реплики, и старый ответ не выглядит
   *  ответом на новый вопрос (замечание Codex-ревью). */
  private async settleReveal(): Promise<void> {
    this.revealEpoch += 1;
    // Новая реплика посетителя — отложенное «печатает…» прошлого ожидания больше
    // не нужно: read-таймер и цикл набора гасим вместе с дотсом, чтобы они не
    // вспыхнули поверх новой отправки; индикатор поднимет уже awaitReply нового
    // ожидания — после свежей паузы «на прочтение».
    this.clearReadTimer();
    this.stopTypingCycle();
    this.hideTypingBubble();
    this.flushHeldMessages();
    this.sleepBreakers.splice(0).forEach((breaker) => breaker());
    await this.revealChain; // без таймеров цепочка дорисовывается за микротаски
  }

  // --- Галочки доставки/прочтения у сообщений посетителя --------------------

  /** «✓/✓✓» рисованным SVG (порт из marketpult): два отдельных текстовых глифа
   *  читаются как случайные значки, а перехлёст двух галочек — как у мессенджеров.
   *  createElementNS — виджет не трогает innerHTML.
   *
   *  @param seen true → ✓✓ (прочитано: ассистент ответил / оператор открыл),
   *    false → ✓ (доставлено: сервер принял реплику, есть id). */
  private tickIcon(seen: boolean): SVGSVGElement {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 17 11');
    svg.setAttribute('width', seen ? '17' : '11');
    svg.setAttribute('height', '11');
    svg.setAttribute('aria-hidden', 'true');
    const stroke = (d: string): void => {
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', d);
      svg.appendChild(path);
    };
    stroke('M1 6.1 L4.2 9.3 L9.7 1.9');
    if (seen) stroke('M7.3 9.3 L12.8 1.9');
    return svg;
  }

  private buildTicks(seen: boolean): HTMLSpanElement {
    const ticks = this.el('span', seen ? 'ticks delivered' : 'ticks');
    ticks.appendChild(this.tickIcon(seen));
    return ticks;
  }

  /** Держим непрочитанный пузырь посетителя в списке ожидания: как только
   *  поллинг сообщит, что реплика с этим id прочитана, поднимаем ✓✓ (applySeen).
   *  Уже помеченный ✓✓ пузырь не отслеживаем. */
  private trackTick(id: number, bubble: HTMLDivElement): void {
    const ticks = bubble.querySelector('.ticks');
    if (!(ticks instanceof HTMLSpanElement) || ticks.classList.contains('delivered')) return;
    this.unseenTicks.push({ id, ticks });
  }

  /** Read-receipt виджета (виджет-паритет, волна 2): по ТОЧНОМУ списку seen_ids из
   *  поллинга поднимаем ✓✓ на своих уже отрисованных пузырях (их нет в messages —
   *  они до курсора after). Именно множество, а не порог: лента кросс-канальная,
   *  прочитанные id не обязаны быть непрерывным префиксом (замечание Codex P2).
   *  Флаг, а не эвристика «пришёл ответ»: ✓✓ ставит и ответ ассистента, и
   *  оператор, открывший диалог без ответа. */
  private applySeen(seenIds: number[] | null | undefined): void {
    if (!seenIds?.length) return;
    const seen = new Set(seenIds);
    this.unseenTicks = this.unseenTicks.filter(({ id, ticks }) => {
      if (!ticks.isConnected) return false; // пузырь удалён после ошибки отправки
      if (!seen.has(id)) return true; // ещё не прочитано — держим
      ticks.classList.add('delivered');
      ticks.replaceChildren(this.tickIcon(true));
      return false;
    });
  }

  // --- Непрочитанные: бейдж на пузыре + звук -------------------------------

  /** Ответ пришёл, пока чат свёрнут или вкладка неактивна: бейдж на пузыре и
   *  «динь» — звук только если владелец включил тумблер (условия marketpult).
   *  Плюс авто-переоткрытие окна, чтобы непрочитанный ответ не потерялся. */
  private notifyUnseen(): void {
    if (this.opened && !document.hidden) return;
    this.unseen += 1;
    this.renderBadge();
    if (this.config?.sound) this.ding();
    this.maybeAutoOpenForReply();
  }

  /** Авто-переоткрытие при непрочитанном ответе (виджет-паритет, волна 2): пришёл
   *  ответ, пока чат свёрнут, — раскрываем окно, чтобы гость его увидел. Гейты:
   *  вкладка видима (не всплываем на фоне — сделаем на возврате, onVisibilityChange);
   *  один раз за сессию (replyAutoOpenSpent — не долбим каждый ответ, уважаем, что
   *  гость мог закрыть); autoOpened — не крадём фокус у читающего (как invite). */
  private maybeAutoOpenForReply(): void {
    if (this.opened || document.hidden || this.replyAutoOpenSpent) return;
    this.replyAutoOpenSpent = true;
    this.autoOpened = true;
    this.open();
  }

  private renderBadge(): void {
    this.badge.classList.toggle('hidden', this.unseen === 0);
    this.badge.textContent = this.unseen > 0 ? String(this.unseen) : '';
    this.renderTabBadge();
  }

  /** Непрочитанные дублируются в заголовок вкладки «(N) …» (как мессенджеры):
   *  ответ виден из другого окна без переключения на вкладку. Оригинальный title
   *  сохраняется и восстанавливается, как только чат прочитан (unseen=0 →
   *  open/visibilitychange). Фавиконку хоста НАМЕРЕННО не трогаем: виджет живёт на
   *  ЧУЖИХ сайтах — подмена иконки через canvas инвазивна и «тейнтится» на
   *  кросс-доменном favicon; здесь мы расходимся с marketpult (там домен свой). */
  private renderTabBadge(): void {
    try {
      // Хост мог сам сменить title (SPA-навигация): если текущий title не наш
      // прошлый бейдж — пересинхронизируем базу с живым заголовком хоста, чтобы
      // при сбросе бейджа вернуть его, а не устаревший (замечание Codex P2).
      if (document.title !== this.lastAppliedTitle) this.baseTitle = document.title;
      const next = tabTitle(this.baseTitle ?? document.title, this.unseen);
      document.title = next;
      this.lastAppliedTitle = next;
    } catch {
      /* среда без document.title — не критично */
    }
  }

  private clearUnseen(): void {
    this.unseen = 0;
    this.renderBadge();
  }

  /** Возврат на вкладку: с открытым чатом ответы уже на экране — бейдж гасим;
   *  живую цепочку поллинга дёргаем сразу — фоновый таймер мог стоять на
   *  медленном кадансе, а посетитель снова смотрит на чат. */
  private onVisibilityChange(): void {
    // Слушатель переживает снос виджета (он на document): выключённый блокировкой
    // виджет не должен на возврате вкладки ни стучать presence, ни поллить.
    if (this.disabled) return;

    this.syncTeaserTyping(); // на скрытой вкладке фразы не печатаются вхолостую
    if (document.hidden) {
      // Вкладка спрятана — отметим уход beacon'ом: мобильный Safari часто не
      // даёт pagehide, а это самый честный сигнал «сейчас не на странице».
      this.beatLeaving();
      return;
    }
    if (this.opened) this.clearUnseen();
    // Вернулся на вкладку, а ответ пришёл, пока её не видели, — раскрываем окно
    // (auto-reopen), но всё так же один раз за сессию (replyAutoOpenSpent).
    else if (this.unseen > 0) this.maybeAutoOpenForReply();
    this.beat(); // снова смотрит на страницу — обновим presence сразу
    this.poller.schedule(0);
  }

  // --- Presence «гость на сайте / ушёл» (виджет-паритет, волна 2) -----------

  /** Heartbeat, пока вкладка видима и диалог начат (lastId>0): раз в ~45с шлём
   *  «я на странице» — по свежести отметки кабинет показывает «на сайте»/«был N
   *  назад». Финальный beacon на уходе (pagehide) даёт точное «ушёл»; на скрытой
   *  вкладке не бьёмся (браузер и так троттлит фоновые таймеры). PII не шлём. */
  private startPresence(): void {
    // Ссылку на интервал держит реестр таймеров: обычно виджет живёт весь
    // жизненный цикл страницы, но блокировка (§12) обязана его остановить —
    // иначе heartbeat стучал бы уже после того, как виджет исчез с экрана.
    this.timers.every(PRESENCE_BEAT_MS, () => this.beat());
    window.addEventListener('pagehide', this.beatLeaving);
  }

  /** Один стук, если есть что отмечать: диалог начат, вкладка видима, и с прошлого
   *  стука прошло не меньше PRESENCE_MIN_GAP_MS (ре-стук по visibilitychange не
   *  строчит и не упирается в троттл). Сбой сети — молча ждём следующего тика. */
  private beat(): void {
    if (this.lastId === 0 || document.hidden) return;
    const now = Date.now();
    if (now - this.lastBeatAt < PRESENCE_MIN_GAP_MS) return;
    this.lastBeatAt = now;
    void this.api.postPresence(this.visitorKey).catch(() => undefined);
  }

  /** Финальный presence-beacon на уходе со страницы (sendBeacon переживает
   *  unload). Гейт тот же — только при начатом диалоге; на bfcache-заморозку
   *  (event.persisted) не шлём: страница ещё жива, посетитель не ушёл. */
  private readonly beatLeaving = (event?: PageTransitionEvent): void => {
    if (this.disabled || this.lastId === 0 || event?.persisted) return;
    this.api.presenceBeacon(this.visitorKey);
  };

  // --- Витринные блоки консультанта (Assistants::Scenario) ------------------

  /** Отдать подсказку бота странице — один раз на реплику. Ошибка в чужом
   *  колбэке не должна ломать ленту: сайт волен упасть у себя, чат продолжает
   *  работать. */
  private deliverPatch(message: ChatMessage): void {
    const patch = message.patch;
    if (!patch || this.patchedIds.has(message.id) || this.patchListeners.length === 0) return;

    this.patchedIds.add(message.id);
    this.patchListeners.forEach((listener) => {
      try {
        listener({ ...patch });
      } catch {
        /* колбэк страницы упал — это её дело, лента живёт дальше */
      }
    });
  }

  /** Коллега вошёл в разговор (§8): бот клиента позвал консультанта платформы.
   *  Меняем ровно две вещи — АДРЕСАТА ленты и ШАПКУ; уже написанное на экране
   *  остаётся, а у коллеги история начинается с чистого (свой диалог в своём
   *  аккаунте), поэтому курсор ленты сбрасываем в 0.
   *
   *  Ошибка конфига не должна ломать чат: адресат уже переключён, шапка просто
   *  останется прежней — гость увидит служебную строку и продолжит разговор. */
  private async applySwitch(blocks?: ChatBlock[]): Promise<void> {
    const block = pickSwitch(blocks);
    if (!block || block.key === this.switchedKey) return;

    this.previousAgent = {
      key: this.switchedKey || this.options.publicKey || '',
      name: this.config!.assistant.name,
    };
    this.switchedKey = block.key;
    this.dropStaleControls();
    this.api.switchKey(block.key);
    this.lastId = 0;
    if (block.text) this.appendBubble('local', block.text);
    try {
      const config = await this.api.fetchConfig(this.visitorKey);
      this.config = config;
      this.headerBox.replaceChildren(
        ...[this.buildIdentity(config.assistant), this.buildBack(), this.buildClose()].filter(
          (node) => node !== null,
        ),
      );
      // Чипы «Продолжить в …» ведут к ботам ПРЕЖНЕГО сотрудника — у коллеги
      // свои каналы (или их нет). Перерисовываем их из нового конфига.
      this.continueChips?.remove();
      this.continueChips = null;
      this.appendContinueChips();
    } catch {
      // Шапка осталась прежней — разговор от этого не прерывается.
    }
  }

  /** Кнопки и форма, нарисованные ДО передачи разговора, обращаются к
   *  `this.api` — то есть после смены адресата ушли бы к коллеге: заявка
   *  прежнего сотрудника легла бы в чужой аккаунт, а «Нанять»/чип отправил бы
   *  его сценарий не тому боту (замечание Codex). Поэтому старые управляющие
   *  элементы убираем: лента-переписка остаётся, интерактив — нет. */
  /** Назад к прежнему сотруднику: зеркало applySwitch. История коллеги
   *  остаётся на экране (гость видит весь разговор), а лента снова слушает
   *  прежний диалог — его сообщения уже нарисованы, поэтому lastId = 0 ничего
   *  не задваивает (renderedIds помнит их по id). */
  private async switchBack(): Promise<void> {
    const target = this.previousAgent;
    if (!target) return;

    this.previousAgent = null;
    this.dropStaleControls();
    this.api.switchKey(target.key);
    // Вернулись к своему боту — снимаем метку переключения, чтобы коллегу можно
    // было позвать снова (второй такой же блок иначе не сработает).
    this.switchedKey = target.key === (this.options.publicKey ?? '') ? '' : target.key;
    this.lastId = 0;
    try {
      const config = await this.api.fetchConfig(this.visitorKey);
      this.config = config;
      this.headerBox.replaceChildren(this.buildIdentity(config.assistant), this.buildClose());
      this.continueChips?.remove();
      this.continueChips = null;
      this.appendContinueChips();
      this.appendBubble('local', this.t('switch_back_done').replace('%{name}', config.assistant.name));
    } catch {
      // Шапка осталась прежней — разговор от этого не прерывается.
    }
  }

  private dropStaleControls(): void {
    this.closeLeadForm();
    this.lastLeadCta?.remove();
    this.lastLeadCta = null;
    this.messagesBox
      .querySelectorAll('.lead-cta, .actions, .quick:not(.continue), .roster')
      .forEach((node) => node.remove());
  }

  private appendBlocks(blocks: ChatBlock[]): void {
    blocks.forEach((block) => this.appendBlock(block));
    this.keepContinueChipsLast();
    this.scrollDown();
  }

  /** Один блок ленты. Незнакомый тип тихо пропускаем (isRenderableBlock):
   *  бандл кешируется на чужих сайтах, а сервер добавляет новые возможности
   *  сотрудника — старый виджет обязан это пережить, а не сломать ленту. */
  private appendBlock(block: ChatBlock): void {
    if (!isRenderableBlock(block)) return;

    if (block.type === 'roster') this.appendRoster(block.items);
    else if (block.type === 'gallery') this.appendGallery(block.items);
    else if (block.type === 'shop') this.appendShop(block.items, block.shop);
    else if (block.type === 'payment') this.appendPayment(block.items);
    else this.appendActions(block.items, block.type);
  }

  /** Фото-карточки работ владельца (маркер [[галерея]]): горизонтальная лента
   *  «миниатюра + заголовок + мета» внутри своего контейнера — панель узкая
   *  (360px), поэтому скролл живёт в ленте, а не у страницы хоста.
   *  Карточка — <a> на само фото (открыть крупно в новой вкладке): доступное
   *  имя даёт aria-label, alt картинки — заголовок карточки. loading="lazy" —
   *  виджет на чужом сайте не тянет всю дюжину фото сразу. */
  private appendGallery(items: GalleryCard[]): void {
    const cards = galleryCards(items);
    if (cards.length === 0) return;

    const strip = this.el('div', 'gallery');
    strip.setAttribute('role', 'group');
    strip.setAttribute('aria-label', this.t('gallery'));
    cards.forEach((card, index) => strip.appendChild(this.galleryCardNode(card, cards, index)));
    this.messagesBox.appendChild(strip);
  }

  /** QR на оплату (маркер [[оплата]]): одна крупная картинка и подпись
   *  владельца под ней. Не лента: код здесь один, и его наводят камерой —
   *  значит он должен быть максимально большим в узкой панели, а не
   *  миниатюрой в скроллере. Клик открывает картинку целиком тем же
   *  просмотрщиком, что и фото работ: с чужого телефона удобнее увеличить.
   *
   *  Оплату подтверждает живой сотрудник — виджет ничего не проверяет и
   *  никаких данных карты не касается: он показывает картинку, и только. */
  private appendPayment(items: PaymentCard[]): void {
    const card = items.find((item) => item && typeof item.image === 'string' && item.image.length > 0);
    if (!card) return;

    const box = this.el('div', 'qr');
    box.setAttribute('role', 'group');
    box.setAttribute('aria-label', this.t('payment'));

    const link = this.el('a', 'qr-image');
    link.href = card.image;
    link.target = '_blank';
    link.rel = 'noopener';
    link.setAttribute('aria-label', this.t('payment_open'));
    const image = document.createElement('img');
    image.src = card.image;
    image.alt = this.t('payment');
    image.loading = 'lazy';
    link.appendChild(image);
    box.appendChild(link);

    if (card.note) box.appendChild(this.el('div', 'qr-note', card.note));
    this.messagesBox.appendChild(box);
  }

  /** Карточки товаров магазина (маркер [[товары:запрос]]): та же лента, что у
   *  работ, но с ценой и кнопками. Фото открывается тем же просмотрщиком —
   *  товар разглядывают ровно так же, как работу.
   *
   *  Кнопка «В корзину» появляется, только если корзина ДОСТИЖИМА: владелец
   *  описал её в кабинете или страница объявила window.assiistentShop. Иначе
   *  карточка остаётся ссылкой на товар — обещать кнопку, которая никуда не
   *  кладёт, хуже, чем её не рисовать. */
  private appendShop(items: ShopCard[], config?: ShopConfig): void {
    const cards = shopCards(items);
    if (cards.length === 0) return;

    const strip = this.el('div', 'gallery shop');
    strip.setAttribute('role', 'group');
    strip.setAttribute('aria-label', this.t('shop'));
    // Просмотрщик знает только фото-карточки, поэтому товары едут в него в
    // его форме: цена показывается подписью, как мета у работ.
    const viewer: GalleryCard[] = cards.map((card) => ({
      image: card.image,
      title: card.title,
      meta: card.price,
    }));
    cards.forEach((card, index) => strip.appendChild(this.shopCardNode(card, viewer, index, config)));
    this.messagesBox.appendChild(strip);
    this.appendCheckoutRow(config, this.messagesBox);
  }

  /** Одна карточка товара: фото (в просмотрщик), название, цена, кнопки. */
  private shopCardNode(
    card: ShopCard,
    viewer: GalleryCard[],
    index: number,
    config?: ShopConfig,
  ): HTMLDivElement {
    const box = this.el('div', 'gallery-card shop-card');
    const photo = this.el('a', 'shop-photo');
    photo.href = card.url || card.image;
    photo.target = '_blank';
    photo.rel = 'noopener noreferrer';
    photo.setAttribute('aria-label', card.price ? `${card.title} · ${card.price}` : card.title);
    photo.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      this.lightboxFor().open(viewer, index);
    });
    const img = document.createElement('img');
    img.src = card.image;
    img.alt = card.title;
    img.loading = 'lazy';
    img.decoding = 'async';
    photo.appendChild(img);

    const body = this.el('div', 'gallery-body');
    body.appendChild(this.el('div', 'gallery-title', card.title));
    if (card.price) body.appendChild(this.el('div', 'gallery-price', card.price));
    box.append(photo, body, this.shopButtons(card, config));
    return box;
  }

  /** Кнопки карточки: «В корзину» (если корзина достижима) и «Открыть». */
  private shopButtons(card: ShopCard, config?: ShopConfig): HTMLDivElement {
    const row = this.el('div', 'shop-actions');
    if (this.cartReachable(card, config)) {
      const add = this.el('button', 'shop-btn primary', this.t('shop_add'));
      add.type = 'button';
      add.addEventListener('click', () => {
        void this.addToCart(card, config, add);
      });
      row.appendChild(add);
    }
    if (card.url) {
      const open = this.el('a', 'shop-btn', this.t('shop_open'));
      open.href = card.url;
      open.target = '_blank';
      open.rel = 'noopener noreferrer';
      row.appendChild(open);
    }
    return row;
  }

  /** Строка «Оформить заказ» под лентой: ведёт на оформление САМОГО магазина —
   *  адрес, время и оплата живут там, где они уже работают. */
  private checkoutRow(url: string): HTMLDivElement {
    const row = this.el('div', 'shop-checkout');
    const link = this.el('a', 'shop-btn primary', this.t('shop_checkout'));
    link.href = url;
    row.appendChild(link);
    return row;
  }

  /** Кнопка оформления под лентой: адрес владельца, а без него — путь, который
   *  знает адаптер движка. Адаптер догружается асинхронно, поэтому место под
   *  кнопку занимаем сразу (порядок ленты), а саму кнопку дописываем, когда
   *  адрес известен. Ни адреса, ни адаптера — кнопки нет. */
  private appendCheckoutRow(config: ShopConfig | undefined, container: HTMLElement): void {
    if (!config) return;
    if (config.checkout_url) {
      container.appendChild(this.checkoutRow(config.checkout_url));
      return;
    }
    if (!config.adapter) return;
    const slot = this.el('div', 'shop-checkout-slot');
    container.appendChild(slot);
    void this.adapterFor(config).then((adapter) => {
      if (adapter?.checkoutPath) slot.appendChild(this.checkoutRow(adapter.checkoutPath));
      else slot.remove();
    });
  }

  /** Есть ли куда класть: мост страницы, адаптер движка или описанная
   *  владельцем корзина. */
  private cartReachable(card: ShopCard, config?: ShopConfig): boolean {
    return Boolean(this.pageCart()) || Boolean(config?.adapter) || cartFields(card, config) !== null;
  }

  /** Адаптер движка магазина (doc/shop_adapters.md): догружается один раз и
   *  только когда владелец его выбрал. Имя — из блока, а без него из конфига
   *  виджета (страница оформления, где блоков ещё нет). */
  private adapterFor(config?: ShopConfig | null): Promise<ShopAdapter | null> {
    const name = config?.adapter ?? this.config?.shop?.adapter;
    if (!name) return Promise.resolve(null);
    this.shopAdapter ??= loadShopAdapter(name, this.api.origin);
    return this.shopAdapter;
  }

  /** Мост, объявленный самой страницей (doc/site_chat_prefill.md): сайту виднее,
   *  как класть в свою корзину, поэтому его функция приоритетнее настроек. */
  private pageCart():
    ((item: { id?: string; title: string; url?: string; quantity?: number }) => unknown) | null {
    const shop = (window as unknown as { assiistentShop?: { addToCart?: unknown } }).assiistentShop;
    return typeof shop?.addToCart === 'function'
      ? (shop.addToCart as (item: { id?: string; title: string; url?: string; quantity?: number }) => unknown)
      : null;
  }

  /** Клик «В корзину»: кладём, показываем результат на самой кнопке и будим
   *  счётчик магазина его же событием. Ошибку не проглатываем — человек должен
   *  видеть, что товар НЕ добавлен, иначе он уйдёт оформлять пустую корзину. */
  private async addToCart(
    card: ShopCard,
    config: ShopConfig | undefined,
    button: HTMLButtonElement,
  ): Promise<void> {
    button.disabled = true;
    const ok = await this.putInCart(card, config);
    button.textContent = this.t(ok ? 'shop_added' : 'shop_error');
    button.classList.toggle('done', ok);
    if (!ok) {
      button.disabled = false;
      return;
    }
    // Сайты обновляют бейдж корзины по своему событию — шлём его, чтобы
    // счётчик в шапке ожил так же, как после клика по кнопке самого магазина.
    document.body.dispatchEvent(new CustomEvent('cartUpdated'));
    this.emitTrack('cart_add');
  }

  /** @return true — товар в корзине магазина. */
  private putInCart(card: ShopCard, config?: ShopConfig): Promise<boolean> {
    const item = { id: card.id ?? '', quantity: 1, title: card.title, url: card.url };
    return this.putInCartItem(item, config, cartFields(card, config));
  }

  /** Одна позиция — в корзину сайта, по старшинству путей: мост страницы →
   *  адаптер движка → поля «своего сайта» (Assistants::ShopSettings).
   *
   *  @param fields поля запроса для «своего сайта» (null — этим путём класть
   *    нечем; мост и адаптер полей не ждут). */
  private async putInCartItem(
    item: CartItemRequest,
    config: ShopConfig | undefined,
    fields: Record<string, string> | null,
  ): Promise<boolean> {
    const bridge = this.pageCart();
    if (bridge) {
      try {
        await bridge({ id: item.id, title: item.title ?? '', url: item.url, quantity: item.quantity });
        return true;
      } catch {
        return false;
      }
    }
    const adapter = await this.adapterFor(config);
    if (adapter) return adapter.addToCart(item).catch(() => false);
    if (!fields || !config?.cart_url) return false;

    try {
      const response = await fetch(config.cart_url, {
        method: 'POST',
        // Корзина магазина держится его же сессией: без кук положить некуда.
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: cartBody(fields, await this.cartToken(item)),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /** Подтверждённый заказ — в корзину САЙТА (message.cart, Orders::Cart):
   *  список позиций с отметками и кнопка «Оформить заказ». Кладёт виджет —
   *  сессией самого посетителя и ОДИН раз на сообщение: история
   *  перечитывается при каждой загрузке, а состав не должен уехать в корзину
   *  дважды (метка — id сообщения в localStorage). Кнопку оформления на
   *  сайте нажимает человек. */
  private appendCart(message: ChatMessage): void {
    const items = cartItems(message.cart?.items);
    if (items.length === 0) return;
    const config = message.cart?.shop ?? this.config?.shop ?? undefined;
    this.lastCart = items;

    const fresh = this.cartFreshIds.has(message.id);
    const box = this.el('div', 'cart-sync');
    box.setAttribute('role', 'status');
    const title = this.el('div', 'cart-sync-title', this.t(fresh ? 'shop_cart_pending' : 'shop_cart_title'));
    box.appendChild(title);
    // История (не fresh) — нейтральный список без отметок: что легло, а что
    // нет, при прошлой загрузке уже было показано, а врать ✓ всем нельзя.
    const rows = items.map((item) => {
      const row = this.el('div', 'cart-row');
      const mark = this.el('span', 'cart-row-mark', fresh ? '…' : '•');
      row.append(mark, this.el('span', 'cart-row-title', `${item.title} × ${item.quantity}`));
      box.appendChild(row);
      return { row, mark };
    });
    this.messagesBox.appendChild(box);
    if (fresh) {
      this.cartFreshIds.delete(message.id);
      void this.syncCart(items, config, rows, title, box);
    } else {
      this.appendCheckoutRow(config, box);
    }
    this.scrollDown();
  }

  /** Кладём позиции по одной и отмечаем каждую: ✓ — в корзине, ✗ — не вышло
   *  (человек добавит её на сайте сам). План — по тому, что уже клали
   *  (cartPlan): гость поправил состав и подтвердил снова — движку, который
   *  прибавляет, уезжает только прирост. Хоть одна легла — будим счётчик
   *  магазина его же событием и рисуем «Оформить заказ». */
  private async syncCart(
    items: CartItem[],
    config: ShopConfig | undefined,
    rows: { row: HTMLDivElement; mark: HTMLSpanElement }[],
    title: HTMLDivElement,
    box: HTMLDivElement,
  ): Promise<void> {
    const adapter = await this.adapterFor(config);
    let state = this.cartState();
    const plan = cartPlan(items, state, Boolean(adapter?.setsQuantity));
    let added = 0;
    for (let index = 0; index < plan.length; index += 1) {
      const { item, send } = plan[index];
      const { row, mark } = rows[index];
      // Последовательно, а не разом: корзина магазина — одна сессия, и
      // параллельные запросы у части движков затирают друг друга.
      const portion = { ...item, quantity: send };
      const ok = send === 0 || (await this.putInCartItem(portion, config, orderCartFields(portion, config)));
      row.classList.add(ok ? 'ok' : 'failed');
      mark.textContent = ok ? '✓' : '✗';
      if (ok) {
        state = cartStateWith(state, item);
        this.saveCartState(state);
        if (send > 0) added += 1;
      } else {
        row.appendChild(this.el('span', 'cart-row-hint', this.t('shop_cart_failed')));
      }
    }
    title.textContent = this.t('shop_cart_title');
    if (added > 0) {
      document.body.dispatchEvent(new CustomEvent('cartUpdated'));
      this.emitTrack('cart_add');
    }
    this.appendCheckoutRow(config, box);
    this.scrollDown();
  }

  /** Что уже клали: localStorage, а при его недоступности — память вкладки
   *  (берём более свежее по id сообщения). */
  private cartState(): CartState {
    let stored: CartState | null = null;
    try {
      stored = readCartState(window.localStorage.getItem(cartStorageKey(this.visitorKey)));
    } catch {
      /* приватный режим — держимся памяти вкладки */
    }
    const memory = this.cartMemory;
    if (!stored) return memory ?? readCartState(null);
    if (!memory) return stored;
    return memory.id >= stored.id ? memory : stored;
  }

  private saveCartState(state: CartState): void {
    this.cartMemory = state;
    try {
      window.localStorage.setItem(cartStorageKey(this.visitorKey), writeCartState(state));
    } catch {
      /* приватный режим — состояние живёт до перезагрузки */
    }
  }

  /** Страница оформления заказа сайта: адрес владельца из конфига или ответ
   *  адаптера движка. Считается один раз на загрузку. */
  private onCheckoutPage(): Promise<boolean> {
    this.checkoutPage ??= (async () => {
      const shop = this.config?.shop;
      if (!shop) return false;
      const trim = (path: string): string => path.replace(/\/+$/, '') || '/';
      if (shop.checkout_url && trim(shop.checkout_url) === trim(window.location.pathname)) return true;
      const adapter = await this.adapterFor(shop);
      return Boolean(adapter?.isCheckout(window.location));
    })();
    return this.checkoutPage;
  }

  /** На странице оформления лента нужна и свёрнутому виджету: без неё нет
   *  ни состава, ни контактов для формы. Один запрос — дальше обычные
   *  правила поллинга (замечание ревью 05.09.2026: до этого форма
   *  заполнялась только у гостя, который сам открыл чат на /order). */
  private pollIfCheckout(): void {
    void this.onCheckoutPage().then((checkout) => {
      if (checkout) void this.poll();
    });
  }

  /** Форма заказа САЙТА — тем, что гость уже назвал в чате (contact), плюс
   *  пометка о составе в комментарии. Только на странице оформления, только
   *  ПУСТЫЕ поля и один раз за загрузку; «Оформить заказ» нажимает человек.
   *  Где форма и как она устроена, знает адаптер движка — без него чужую
   *  форму не трогаем. */
  private async fillCheckoutForm(): Promise<void> {
    if (this.checkoutFilled) return;
    const adapter = await this.adapterFor(this.config?.shop);
    if (!adapter || !adapter.isCheckout(window.location)) return;
    const data = checkoutData(this.contact, this.lastCart, this.t('shop_cart_comment'));
    if (Object.keys(data).length === 0) return;
    if (adapter.fillCheckout(data, document) > 0) {
      this.checkoutFilled = true;
      this.emitTrack('checkout_prefill');
    }
  }

  /** Поле защиты для запроса в корзину: со страницы, а если её форма «В корзину»
   *  не содержит (главная, «О нас»), — со страницы самого товара. Она у карточки
   *  есть всегда и форму содержит по определению. Не достали — идём без токена:
   *  магазину, который его не требует, он и не нужен. */
  private async cartToken(card: { url?: string }): Promise<Record<string, string>> {
    const local = csrfField(document);
    if (!needsRemoteToken(local, card)) return local;

    try {
      const html = await fetch(card.url as string, { credentials: 'same-origin' }).then((r) => r.text());
      return csrfField(new DOMParser().parseFromString(html, 'text/html'));
    } catch {
      return local;
    }
  }

  /** Просмотрщик фото (lightbox.ts) собирается при первом открытии, когда
   *  корень виджета уже смонтирован; здесь — только адаптер хоста. */
  private lightboxFor(): Lightbox {
    this.lightboxView ??= new Lightbox({
      el: (tag, className, text) => this.el(tag, className, text),
      t: (key) => this.t(key),
      root: this.root,
      activeElement: () => this.shadow.activeElement as HTMLElement | null,
    });
    return this.lightboxView;
  }

  private galleryCardNode(card: GalleryCard, cards: GalleryCard[], index: number): HTMLAnchorElement {
    const box = this.el('a', 'gallery-card');
    // href остаётся настоящим адресом фото: средний клик и «открыть в новой
    // вкладке» должны вести туда же, куда ведёт обычная ссылка.
    box.href = card.image;
    box.target = '_blank';
    box.rel = 'noopener noreferrer';
    box.addEventListener('click', (event) => {
      // Обычный клик — просмотрщик поверх страницы; клик с модификатором
      // (новая вкладка/окно) не перехватываем: это осознанный выбор человека.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      this.lightboxFor().open(cards, index);
    });
    box.setAttribute('aria-label', galleryCardLabel(card));
    const img = document.createElement('img');
    img.src = card.image;
    img.alt = card.title;
    img.loading = 'lazy';
    img.decoding = 'async';
    const body = this.el('div', 'gallery-body');
    body.appendChild(this.el('div', 'gallery-title', card.title));
    // Цену показываем отдельной строкой (splitPrice): в общей подписи через «·»
    // она стояла последней и тонула. Само значение — как написал владелец.
    const { price, rest } = splitPrice(card.meta);
    if (rest) body.appendChild(this.el('div', 'gallery-meta', rest));
    if (price) body.appendChild(this.el('div', 'gallery-price', price));
    box.append(img, body);
    return box;
  }

  /** Витрина сотрудников: горизонтальная лента карточек (фото, роль, «Нанять»). */
  private appendRoster(items: RosterCard[]): void {
    const strip = this.el('div', 'roster');
    items.forEach((card) => {
      const box = this.el('div', 'roster-card');
      const photo = this.el('div', 'roster-photo');
      if (card.avatar) {
        const img = document.createElement('img');
        img.src = card.avatar;
        img.alt = card.name;
        img.loading = 'lazy';
        photo.appendChild(img);
      }
      photo.appendChild(this.el('span', 'roster-online', this.t('online')));
      const body = this.el('div', 'roster-body');
      body.append(
        this.el('div', 'roster-name', card.name),
        this.el('div', 'roster-role', card.role),
        this.el('div', 'roster-desc', card.description),
      );
      if (card.honesty) body.appendChild(this.el('div', 'roster-honesty', card.honesty));
      const hire = this.el('a', 'roster-hire', card.hire.label);
      hire.href = card.hire.url;
      hire.target = '_blank';
      hire.rel = 'noopener';
      body.appendChild(hire);
      box.append(photo, body);
      strip.appendChild(box);
    });
    this.messagesBox.appendChild(strip);
  }

  /** Кнопки-действия (столбик) или чипы быстрых ответов (лента): `url` —
   *  ссылка в новой вкладке, `send` — отправка текста как нового сообщения. */
  private appendActions(items: ChatAction[], kind: 'actions' | 'quick'): void {
    const wrap = this.el('div', kind);
    items.forEach((item) => {
      const cls = kind === 'quick' ? 'quick-chip' : `action-btn ${item.style ?? 'secondary'}`;
      if (item.url) {
        wrap.appendChild(this.linkNode(item.url, item.label, cls));
      } else {
        const btn = this.el('button', cls, item.label);
        btn.type = 'button';
        const text = item.send ?? item.label;
        btn.addEventListener('click', () => void this.send(text));
        wrap.appendChild(btn);
      }
    });
    this.messagesBox.appendChild(wrap);
  }

  /** Текст с автоссылками: текстовые узлы и <a> на каждый https-адрес. */
  private fillLinked(node: HTMLElement, text: string): void {
    linkify(text).forEach((part) => {
      if (part.url) node.appendChild(this.linkNode(part.url, part.text, 'msg-link'));
      else node.appendChild(document.createTextNode(part.text));
    });
  }

  /** Ссылка ленты (кнопка «Открыть страницу», автоссылка, действие сценария):
   *  новая вкладка — или, у консультанта на своём сайте, переход в этом окне
   *  с авто-переоткрытием чата на следующей странице; на холодном демо — в
   *  рамку с сайтом цели, чтобы чат остался на месте (navigation.ts). */
  private linkNode(url: string, label: string, className: string): HTMLAnchorElement {
    const link = this.el('a', className, label);
    link.href = url;
    const frame = opensInFrame(this.navigationContext()) ? this.demoFrame() : null;
    if (frame) {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        frame.src = url;
      });
    } else if (this.opensInPlace(url)) {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.navigateInPlace(url);
      });
    } else {
      link.target = '_blank';
      link.rel = 'noopener';
    }
    return link;
  }

  /** Рамка холодного демо на странице-хозяине. Её нет — значит виджет стоит не
   *  на демо (кто-то поставил ключ демо к себе), и ссылка уйдёт во вкладку. */
  private demoFrame(): HTMLIFrameElement | null {
    return document.querySelector('iframe.demo-frame');
  }

  private opensInPlace(url: string): boolean {
    return opensInPlace(url, this.navigationContext());
  }

  private navigationContext(): NavigationContext {
    return {
      pageTarget: this.config?.page_target,
      launcher: this.options.launcher,
      origin: window.location.origin,
    };
  }

  /** Переход в этом окне: метка «переоткрыть чат» живёт до конца визита —
   *  на следующей странице виджет откроется сам, и разговор продолжится с того
   *  же места (история — по ключу посетителя). */
  private navigateInPlace(url: string): void {
    try {
      window.sessionStorage.setItem(this.reopenKey(), '1');
    } catch {
      /* приватный режим — откроют пузырём */
    }
    window.location.assign(url);
  }

  /** Гость перешёл по ссылке из чата в этом окне — открываем чат на новой
   *  странице сразу, без пузыря и без задержки автооткрытия. Метка одноразовая. */
  private openAfterNavigation(): void {
    if (this.consumeReopenFlag() && !this.opened) this.open();
  }

  private consumeReopenFlag(): boolean {
    try {
      const asked = window.sessionStorage.getItem(this.reopenKey()) === '1';
      if (asked) window.sessionStorage.removeItem(this.reopenKey());
      return asked;
    } catch {
      return false;
    }
  }

  private reopenKey(): string {
    return `assiistent_reopen_${this.options.publicKey ?? ''}`;
  }

  /** Событие воронки сайт-чата. Виджет — единственный источник целей:
   *  1) автодетект Метрики хоста — reachGoal (site_chat_*) в счётчик страницы
   *     напрямую (и на сайте клиента, и на нашем ассииистент.рф), без настройки
   *     в кабинете и без задвоения из лендинг-JS;
   *  2) CustomEvent 'assistant-widget:track' — только для внешней совместимости
   *     (сторонняя аналитика клиента); наши цели из него больше не шлём. */
  private emitTrack(action: string): void {
    this.reportMetrika(action);
    try {
      document.dispatchEvent(new CustomEvent('assistant-widget:track', { detail: { action } }));
    } catch {
      /* среда без CustomEvent — не критично */
    }
  }

  /** Контекст посетителя: источник перехода, таймзона и язык браузера — для
   *  «человечных» ответов (бот может по-доброму обыграть местоположение/время). */
  private visitorContext(): VisitorContext {
    let timezone = '';
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
      /* Intl недоступен — без таймзоны */
    }
    return {
      referrer: document.referrer || '',
      timezone,
      locale: navigator.language || '',
      device: this.detectDevice(),
      metrika_client_id: this.metrikaClientId,
      metrika_counter_id: this.metrikaCounterId,
      ad_click: this.fromPaidClick(),
    };
  }

  /** Грубая классификация устройства (моб/комп) — оператору в кабинете. */
  private detectDevice(): 'mobile' | 'desktop' {
    const ua = navigator.userAgent || '';
    const touchMac = navigator.maxTouchPoints > 1 && /Macintosh/.test(ua); // iPadOS
    return /Mobi|Android|iPhone|iPod|iPad/i.test(ua) || touchMac ? 'mobile' : 'desktop';
  }

  private sendMessage(): Promise<void> {
    return this.send(this.input.value);
  }

  /** Отправка сообщения: из поля ввода (sendMessage) или из чипа/кнопки блока
   *  (передаём текст напрямую — он снова ловится сценарием на сервере).
   *  Вложения-чипы едут вместе с текстом; file-only отправка (пустой текст,
   *  есть файлы) разрешена — как в marketpult. */
  private async send(raw: string): Promise<void> {
    const text = raw.trim();
    const files = [...this.pendingFiles];
    if ((!text && files.length === 0) || this.sending || this.limitReached) return;

    // До первого await: permission prompt должен стартовать, пока жив жест
    // клика/Enter (transient activation) — после await отправки Firefox его
    // глушит (замечание Codex-ревью).
    this.maybeSubscribePush();
    this.sending = true;
    this.sendButton.disabled = true;
    this.input.value = '';
    this.autosizeInput(); // поле схлопывается в одну строку, крестик очистки прячется
    // Недораскрытый ответ дорисовывается мгновенно ПЕРЕД репликой посетителя —
    // порядок ленты логичный, эхо не задерживается (таймеры прерваны).
    await this.settleReveal();
    const bubble = this.appendBubble('visitor', text);
    const filesBox = files.length > 0 ? this.appendLocalFiles(bubble, files) : null;

    try {
      const response = await this.api.postMessage(
        this.visitorKey,
        text,
        window.location.pathname,
        this.visitorContext(),
        files,
        this.formContext,
      );
      this.renderedIds.add(response.message.id);
      this.lastId = Math.max(this.lastId, response.message.id);
      this.markReturning(); // отправил реплику → в следующий визит гость «вернувшийся»
      // Эхо с id = доставлено (✓). Дальше следим за пузырём: прочтение (✓✓)
      // поднимет applySeen по списку seen_ids на следующем поллинге.
      this.trackTick(response.message.id, bubble);
      // Локальное эхо файлов → ссылки сервера (подписанные URL из ответа).
      filesBox?.replaceChildren(...(response.message.files ?? []).map((file) => this.fileNode(file)));
      // Из чипов уходят только ОТПРАВЛЕННЫЕ файлы: выбранное во время долгой
      // загрузки остаётся ждать следующей отправки (замечание Codex-ревью).
      this.pendingFiles = this.pendingFiles.filter((file) => !files.includes(file));
      this.renderFileChips();
      this.hideBanner();
      // Цель ждёт ответа бота: на оффтопе она не уйдёт вовсе (см. поле выше).
      this.pendingMessageSent = true;
      // Диалог завязался: посетитель пишет снова, уже прочитав профильный ответ
      // бота. Цель уходит один раз на посетителя — дедуп внутри emitTrack.
      if (shouldTrackVisitorReply(this.botAnsweredOnTopic, true)) this.emitTrack('reply');
      this.awaitReply();
    } catch (error) {
      // Отправка не прошла: текст возвращается в поле, чипы файлов на месте.
      bubble.remove();
      this.input.value = text;
      this.autosizeInput(); // поле снова растёт под возвращённый текст, крестик виден
      this.handleSendError(error);
    } finally {
      this.sending = false;
      this.sendButton.disabled = this.limitReached;
    }
  }

  private handleSendError(error: unknown): void {
    if (error instanceof ApiError && error.status === 404) {
      this.disable();
      return;
    }
    if (error instanceof ApiError && error.status === 402) {
      // Лимит тарифа исчерпан (spec §11) — нудж владельцу показывает кабинет,
      // посетителю мягкий текст; ввод закрываем до перезагрузки страницы.
      this.limitReached = true;
      this.input.disabled = true;
      this.showBanner(error.message || this.t('error_generic'), false);
      return;
    }
    if (error instanceof ApiError) {
      this.showBanner(error.message || this.t('error_generic'), false);
      return;
    }
    this.showBanner(this.t('offline'), true);
  }

  /** Web Push — один заход за загрузку страницы и только в момент отправки
   *  первой реплики: посетитель начал ждать ответ, и «догоним, даже если
   *  уйдёте» не требует объяснений; prompt на входе — путь к вечному
   *  «Блокировать» (условия marketpult). Без VAPID-ключа сервера — не лезем. */
  private maybeSubscribePush(): void {
    const key = this.config?.push_key;
    if (!key || this.pushAsked) return;
    this.pushAsked = true;
    void subscribeToPush(this.api, this.visitorKey, key);
  }

  private awaitReply(): void {
    // Реплика только что ушла — лента живая: даже если первый поллинг ещё не
    // случился, грядущий ответ должен раскрыться с человеческим темпом.
    this.liveFeed = true;
    this.awaitingReplyUntil = Date.now() + AWAIT_REPLY_TIMEOUT_MS;
    // «Печатает…» НЕ в момент отправки (порт marketpult 66e46ef): собеседник сперва
    // ЧИТАЕТ реплику посетителя. Сначала тишина «на прочтение», затем индикатор.
    this.scheduleTypingAfterRead();
    this.schedulePoll(POLL_INTERVAL_AWAITING_MS);
  }

  /** Поднять «печатает…» через read-паузу (длина последней реплики посетителя).
   *
   *  Пауза работает и под prefers-reduced-motion — решение владельца 30.07.2026:
   *  пауза это ТАЙМИНГ разговора, а не движение на экране. §5 держится там, где
   *  он про движение: точки индикатора не мигают, переходы выключены (медиазапрос
   *  в styles.ts), печать фраз тизера не стартует. А собеседник, отвечающий без
   *  паузы «в ту же миллисекунду», выдаёт робота одинаково в любом режиме. */
  private scheduleTypingAfterRead(): void {
    this.clearReadTimer();
    const delay = readDelayMs(this.lastVisitorText, this.readJitter());
    this.readTimer = this.timers.after(delay, () => {
      this.readTimer = null;
      this.startTypingCycle();
    });
  }

  /** Случайный разброс read-паузы 0.7×–1.3×: фиксированный такт сам по себе выдаёт
   *  робота (marketpult). Вынесен из чистой readDelayMs ради её тестируемости. */
  private readJitter(): number {
    return 0.7 + Math.random() * 0.6;
  }

  private clearReadTimer(): void {
    this.timers.clear(this.readTimer);
    this.readTimer = null;
  }

  /** «Печатает…» пачками, пока ответ в пути: дотс горит случайный burst, гаснет
   *  на случайный rest («думает») и загорается снова — непрерывный индикатор на
   *  всё время генерации выдаёт бота. Цикл гасят replyArrived (ответ пришёл или
   *  ожидание истекло) и settleReveal (посетитель написал новое); таймер живёт
   *  в общем реестре, так что stop() у заблокированного (§12) снимает и его. */
  private startTypingCycle(): void {
    this.stopTypingCycle();
    const burst = (): void => {
      this.showTypingBubble();
      this.typingCycleTimer = this.timers.after(burstMs(Math.random()), rest);
    };
    const rest = (): void => {
      this.hushTypingBubble();
      this.typingCycleTimer = this.timers.after(restMs(Math.random()), burst);
    };
    burst();
  }

  private stopTypingCycle(): void {
    this.timers.clear(this.typingCycleTimer);
    this.typingCycleTimer = null;
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  /** Пузырь «печатает…» в конец ленты: и ожидание ответа (awaitReply), и пауза
   *  между частями многопузырькового ответа (renderContent). Гасит отложенный
   *  read-таймер — дотс, показанный явно, не должен продублироваться.
   *
   *  Чипы «Продолжить в …» возвращаем под него: дотс — это реплика, которая
   *  вот-вот станет пузырём, и её место в ленте, а не под кнопками. Без
   *  возврата он ложился ПОД «Продолжить в Telegram/MAX» и выглядел так, будто
   *  печатают сами кнопки. */
  private showTypingBubble(): void {
    this.clearReadTimer();
    this.typingBubble.classList.remove('hidden', 'hushed');
    this.messagesBox.appendChild(this.typingBubble);
    this.keepContinueChipsLast();
    this.scrollDownIfNear();
  }

  /** Пауза внутри набора: дотс гаснет, но СТРОКУ из ленты не вынимаем.
   *  Пачки burst/rest мигают по нескольку раз за одно ожидание, и раньше
   *  каждое мигание добавляло и убирало ~37 px в конце ленты — весь разговор
   *  прыгал вверх-вниз (жалобы клиентов 23.08.2026). Место остаётся занятым,
   *  меняется только видимость. */
  private hushTypingBubble(): void {
    this.typingBubble.classList.add('hushed');
  }

  /** Ожидание кончилось: строку убираем совсем — её место занимает пузырь
   *  ответа. Это единственная законная смена высоты за весь цикл. */
  private hideTypingBubble(): void {
    this.typingBubble.classList.add('hidden');
    this.typingBubble.classList.remove('hushed');
  }

  private replyArrived(): void {
    this.awaitingReplyUntil = 0;
    // Ответ пришёл раньше, чем истекла read-пауза, — отменяем отложенный дотс,
    // чтобы он не вспыхнул уже после того, как пузырь лёг на экран.
    this.clearReadTimer();
    // Горел ли дотс — запоминаем ДО гашения: по этому флагу renderContent
    // решает, дать ли короткий добор набора перед первым пузырём.
    this.typingSeenAtArrival = !this.typingBubble.classList.contains('hidden');
    this.stopTypingCycle();
    this.hideTypingBubble();
  }

  private scrollDown(): void {
    this.messagesBox.scrollTop = this.messagesBox.scrollHeight;
  }

  /** Прокрутка ради «печатает…»: только если гость и так у нижнего края.
   *  Иначе индикатор утаскивал его вниз каждую пачку — человек не мог
   *  перечитать ответ выше, пока бот «печатает». */
  private scrollDownIfNear(): void {
    const box = this.messagesBox;
    const gap = box.scrollHeight - box.scrollTop - box.clientHeight;
    if (gap <= NEAR_BOTTOM_PX) this.scrollDown();
  }

  // --- Поллинг (MVP; ActionCable — v-next) --------------------------------

  private startPolling(): void {
    this.poller.start();
  }

  private stopPolling(): void {
    this.poller.stop();
  }

  private schedulePoll(delay: number): void {
    this.poller.schedule(delay);
  }

  private async poll(): Promise<void> {
    if (!this.config) return;
    // Страница оформления заказа сайта — единственный случай, когда свёрнутый
    // виджет без начатого диалога идёт за лентой (состав и контакты для
    // формы); флаг едет серверу — по нему он прикладывает контакты.
    const checkout = this.lastId === 0 && (await this.onCheckoutPage());
    if (!this.opened && this.lastId === 0 && !checkout) return;
    // Обратный read-receipt (долг волны виджет-паритета): отметка о прочтении
    // едет ПАРАМЕТРОМ того же поллинга — без нового публичного эндпоинта и
    // лишних запросов (spec §12). Честность семантики: панель открыта И вкладка
    // видима И реплика отрисована (seenReportId + renderedBusinessId).
    const seenThrough = seenReportId(
      this.renderedBusinessId,
      this.reportedSeenId,
      this.opened,
      document.visibilityState === 'visible',
    );
    try {
      const response = await this.api.fetchMessages(this.visitorKey, this.lastId, seenThrough, checkout);
      // Сервер ответил — обычный каданс возвращается сразу (сброс до разбора
      // ответа: blocked и ошибка отрисовки — это живой API, а не лежащий).
      this.pollFailures = 0;
      // Посетителя заблокировали (§12) — «сразу же» это ближайший поллинг:
      // виджет исчезает молча, ленту и прощальную реплику бота не разбираем
      // (объяснение = подсказка, как обойти правила). schedulePoll ниже уже не
      // выполняется — следующего запроса не будет.
      if (response.blocked) {
        this.disable();
        return;
      }
      // Курсор двигаем только по факту принятого запроса: сорванный поллинг
      // отчитается заново на следующем тике.
      if (seenThrough > 0) this.reportedSeenId = seenThrough;
      // ПЕРВАЯ выборка — восстановление истории: рисуем мгновенно, без
      // спектакля набора (как restoreConversation в marketpult). Пейсинг
      // пузырей — только для ответов, пришедших в уже живую ленту.
      const instant = !this.liveFeed;
      this.liveFeed = true;
      // Контакты для формы заказа сайта — только на первой выборке (сервер
      // отдаёт их вместе с историей, см. Widget::MessagesController).
      if (response.contact) this.contact = response.contact;
      response.messages.forEach((message) => this.appendServerMessage(message, instant));
      // Read-receipt: поднимаем ✓✓ на своих пузырях по списку прочитанных. После
      // forEach — свежеотрисованные (другая вкладка) уже отслеживаются trackTick.
      this.applySeen(response.seen_ids);
      // Ре-показ формы контакта после перезагрузки — на ПЕРВОЙ выборке (история):
      // ждём хвост цепочки раскрытия, чтобы CTA последней просьбы уже стояла.
      if (instant) void this.revealChain.then(() => this.reviveContactAsk(response.messages));
      // Форма заказа сайта — после истории: состав (message.cart) и контакты
      // к этому моменту уже известны, а страница оформления загрузилась.
      if (instant) void this.revealChain.then(() => this.fillCheckoutForm());
      if (this.banner.classList.contains('hidden') || this.bannerText.textContent === this.t('offline')) {
        this.hideBanner();
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        this.disable();
        return;
      }
      this.pollFailures += 1;
      this.showBanner(this.t('offline'), true);
    }
    if (this.awaitingReplyUntil > 0 && Date.now() > this.awaitingReplyUntil) this.replyArrived();
  }

  /** Каданс: ждём ответ — быстро; открытый чат на активной вкладке — обычный;
   *  свёрнутый чат или фоновая вкладка — медленный (бейдж/звук не к спеху).
   *  API недоступен — пауза растёт от обычной с каждой неудачей подряд;
   *  ручные опросы (кнопка «Повторить», отправка реплики) идут мимо каданса. */
  private pollDelay(): number {
    if (this.awaitingReplyUntil > 0) return backoffDelayMs(POLL_INTERVAL_AWAITING_MS, this.pollFailures);
    if (this.opened && !document.hidden) return backoffDelayMs(POLL_INTERVAL_MS, this.pollFailures);
    return backoffDelayMs(POLL_INTERVAL_BACKGROUND_MS, this.pollFailures);
  }

  // --- Баннер состояний ----------------------------------------------------

  private showBanner(text: string, retriable: boolean): void {
    this.bannerText.textContent = text;
    this.bannerRetry.classList.toggle('hidden', !retriable);
    this.banner.classList.remove('hidden');
  }

  private hideBanner(): void {
    this.banner.classList.add('hidden');
  }

  // --- Звук входящего (WebAudio-баззер, без внешних файлов) ----------------

  /** Разблокировка WebAudio первым жестом (autoplay-политика браузеров);
   *  «динь», пропущенный до жеста, доигрывается сразу после разблокировки. */
  private readonly unlockAudio = (): void => {
    this.audioContext();
    if (this.pendingDingAt > 0 && Date.now() - this.pendingDingAt < DING_PENDING_TTL_MS) {
      this.pendingDingAt = 0;
      this.timers.after(50, () => this.ding()); // resume() нужен такт
    }
  };

  private audioContext(): AudioContext | null {
    const Ctx = window.AudioContext ?? window.webkitAudioContext;
    if (!Ctx) return null;
    this.audioCtx ??= new Ctx();
    if (this.audioCtx.state === 'suspended') void this.audioCtx.resume();
    return this.audioCtx;
  }

  /** Короткий двухтональный «динь» осцилляторами WebAudio (порт из marketpult):
   *  без звуковых файлов и data-URI, CSP-safe. Пачка ответов не строчит —
   *  не чаще раза в DING_THROTTLE_MS. */
  private ding(): void {
    if (Date.now() - this.lastDingAt < DING_THROTTLE_MS) return;
    const ctx = this.audioContext();
    if (!ctx || ctx.state !== 'running') {
      this.pendingDingAt = Date.now();
      return;
    }
    // Троттл — только по реально сыгранному звуку: иначе «динь», отложенный до
    // первого жеста, сам себя задушит при доигрывании (замечание Codex-ревью).
    this.lastDingAt = Date.now();
    const tones: Array<[number, number]> = [
      [880, 0],
      [1318.5, 0.12],
    ];
    tones.forEach(([freq, delay]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle'; // богаче чистого синуса — слышно на ноутбучных динамиках
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.55);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.6);
    });
  }

  // --- Форма заявки (инлайн в ленте, живая валидация — виджет-паритет, волна 2) -

  /** Раскрыть форму контакта ИНЛАЙН в ленте (чат остаётся виден/прокручиваем):
   *  CTA уступает место форме, экран не заменяется. Уже открытую — проматываем. */
  private openLeadForm(cta?: HTMLButtonElement): void {
    if (this.leadAlreadySubmitted()) return;
    if (this.leadForm?.isConnected) {
      this.scrollDown();
      return;
    }
    cta?.remove(); // CTA свою роль сыграла — форма встаёт на её место
    this.leadForm = this.buildLeadForm();
    this.messagesBox.appendChild(this.leadForm);
    // Постоянные чипы «Продолжить в Telegram/MAX» на время формы прячем: те же
    // две кнопки форма показывает у себя внутри, и рядом они читались как две
    // разные пары (скриншот владельца 03.08.2026).
    this.toggleContinueChips(false);
    this.scrollDown();
  }

  /** Показать/спрятать постоянные чипы «Продолжить в …». Прячем атрибутом
   *  hidden — display у .quick перебивает браузерный дефолт, поэтому в стилях
   *  лежит явное правило [hidden]. */
  private toggleContinueChips(visible: boolean): void {
    if (this.continueChips) this.continueChips.hidden = !visible;
  }

  /** Инлайн-форма: крестик «продолжить чат», мессенджер-кнопки (если каналы
   *  подключены), поля с живой валидацией. Имя и телефон обязательны, почта —
   *  по желанию (правила полей — leadFieldErrorKey в ./contact).
   *
   *  Поля «Комментарий» здесь нет намеренно (решение владельца 03.08.2026):
   *  форма раскрывается ПОСЕРЕДИНЕ разговора — всё, что человек хотел сказать,
   *  он уже написал боту, и второе поле для того же текста только удлиняет
   *  анкету. Заявке комментарий приезжает из диалога (Conversations::SubmitDraft). */
  private buildLeadForm(): HTMLFormElement {
    const form = document.createElement('form');
    form.className = 'lead-form-inline';
    form.noValidate = true;

    const head = this.el('div', 'lead-head');
    head.append(this.el('span', 'lead-title', this.t('lead_title')), this.buildLeadClose());
    form.append(head);
    this.appendLeadMessengers(form);

    const name = this.buildLeadField(form, 'name', 'text', 'name');
    const phone = this.buildLeadField(form, 'phone', 'tel', 'tel');
    const email = this.buildLeadField(form, 'email', 'email', 'email');
    // То, что человек уже назвал боту, подставляем в поля: набирать имя и номер
    // второй раз — ровно тот шаг, на котором заявку и бросают.
    this.fillLeadForm({ name, phone, email });
    [name, phone, email].forEach((field) => this.wireLeadValidation(field));

    const error = this.el('div', 'lead-error');
    error.hidden = true;
    const submit = this.el('button', 'btn lead-submit', this.t('lead_submit'));
    submit.type = 'submit';
    form.append(error, submit);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitLead(form, { name, phone, email, error });
    });
    return form;
  }

  /** Подставить в форму то, что сервер уже знает из разговора (lead_prefill).
   *  Телефон прогоняем через тот же форматтер, что и ручной ввод, — иначе он
   *  выглядит иначе, чем набранный руками. Пустое поле оставляем пустым: наши
   *  догадки не должны выглядеть как ответ человека. */
  private fillLeadForm(fields: Record<LeadFieldKind, HTMLInputElement>): void {
    const known = this.leadPrefill;
    if (!known) return;

    (Object.keys(fields) as LeadFieldKind[]).forEach((key) => {
      const value = known[key];
      if (!value) return;

      fields[key].value = key === 'phone' ? formatRuPhone(value) : value;
    });
  }

  /** Крестик «закрыть форму, продолжить чат» (skip): диалог не теряется. */
  private buildLeadClose(): HTMLButtonElement {
    const close = this.el('button', 'lead-close', '✕');
    close.type = 'button';
    close.setAttribute('aria-label', this.t('lead_skip'));
    close.title = this.t('lead_skip');
    close.addEventListener('click', () => this.closeLeadForm());
    return close;
  }

  /** Путь БЕЗ формы: «Продолжить в Telegram/MAX» первыми (порт marketpult) — тап
   *  в уже открытом мессенджере дешевле анкеты. Переиспользует continue-механику
   *  чипов; каналов нет → блок не рисуем. */
  private appendLeadMessengers(form: HTMLFormElement): void {
    const options = this.config?.continue;
    if (!options?.length) return;
    const wrap = this.el('div', 'lead-messengers');
    options.forEach((option) => {
      const btn = this.el('button', 'lead-messenger', option.label);
      btn.type = 'button';
      btn.addEventListener('click', () => void this.openContinueLink(option.kind, btn));
      wrap.appendChild(btn);
    });
    form.append(wrap, this.el('p', 'lead-or', this.t('lead_or_form')));
  }

  private buildLeadField(
    form: HTMLFormElement,
    key: LeadFieldKind,
    type: string,
    autocomplete: AutoFill,
  ): HTMLInputElement {
    const wrap = this.el('label', 'lead-field');
    const label = this.el('span', 'lead-label', this.t(`lead_${key}`));
    // Звёздочка у обязательных: без неё человек видит «Телефон» и «Электронная
    // почта (необязательно)» и делает вывод, что необязательны оба.
    if (key !== 'email') {
      const mark = this.el('span', 'lead-required', '*');
      mark.setAttribute('aria-hidden', 'true'); // читалке уже сказал aria-required
      label.appendChild(mark);
    }
    wrap.append(label);
    const input = document.createElement('input');
    input.name = key;
    input.type = type;
    input.maxLength = 200;
    input.autocomplete = autocomplete;
    if (type === 'tel' || type === 'email') input.inputMode = type; // клавиатура под номер/почту
    // Обязательность — атрибутом (читалка экрана объявляет «обязательное»), но
    // без браузерной валидации: сообщения браузера мимо стиля виджета и на
    // чужом языке ОС. Судит leadFieldErrorKey, форма — noValidate.
    if (key !== 'email') input.setAttribute('aria-required', 'true');
    if (key === 'phone') input.placeholder = this.t('lead_phone_placeholder');
    // Строку ошибки заводим ВСЕМ полям (текст подставляет validateLeadField —
    // у поля их несколько: «укажите» и «проверьте формат»).
    const err = this.el('span', 'lead-field-error');
    err.hidden = true;
    wrap.append(input, err);
    form.append(wrap);
    return input;
  }

  /** Живая валидация поля: ошибку показываем только после того, как поле
   *  «тронули» (blur) — не орём во время первого набора (порт validateField).
   *  Заодно чистим ввод на лету: имя без цифр и спецсимволов, номер — сразу в
   *  «+7 (912) 345-67-89», чтобы человек видел, что от него ждут, а не набирал
   *  мусор до самой отправки. */
  private wireLeadValidation(field: HTMLInputElement): void {
    field.addEventListener('blur', () => {
      field.dataset.touched = '1';
      this.validateLeadField(field);
    });
    field.addEventListener('input', (event) => {
      this.preformatLeadField(field, event);
      this.validateLeadField(field);
    });
  }

  /** Преформат ввода. Номер НЕ переодеваем на удалении (inputType delete*):
   *  иначе backspace возвращал бы съеденную скобку и поле выглядело бы
   *  заклинившим (та же оговорка, что в форме лендинга). */
  private preformatLeadField(field: HTMLInputElement, event: Event): void {
    if (field.name === 'name') {
      const filtered = filterLeadName(field.value);
      if (filtered !== field.value) field.value = filtered;
      return;
    }
    if (field.name !== 'phone') return;
    if (String((event as InputEvent).inputType ?? '').startsWith('delete')) return;

    field.value = formatRuPhone(field.value);
  }

  /** @return поле в порядке (ошибки нет). */
  private validateLeadField(field: HTMLInputElement): boolean {
    const key = leadFieldErrorKey(field.name as LeadFieldKind, field.value);
    const show = field.dataset.touched === '1' && key !== null;
    field.classList.toggle('invalid', show);
    field.setAttribute('aria-invalid', String(show));
    const error = field.parentElement?.querySelector('.lead-field-error');
    if (error instanceof HTMLElement) {
      if (key) error.textContent = this.t(key);
      error.hidden = !show;
    }
    return key === null;
  }

  /** Крестик формы: убрать её, не теряя диалог — бот позовёт снова, когда уместно. */
  private closeLeadForm(): void {
    this.leadForm?.remove();
    this.leadForm = null;
    this.toggleContinueChips(true); // форма ушла — путь в мессенджер снова на виду
    this.scrollDown();
  }

  private async submitLead(
    form: HTMLFormElement,
    refs: {
      name: HTMLInputElement;
      phone: HTMLInputElement;
      email: HTMLInputElement;
      error: HTMLDivElement;
    },
  ): Promise<void> {
    const { name, phone, email, error } = refs;
    // На сабмите «трогаем» все поля разом: пустое обязательное поле обязано
    // объясниться, даже если в него ни разу не заходили.
    const fields = [name, phone, email];
    fields.forEach((field) => {
      field.dataset.touched = '1';
    });
    const invalid = fields.filter((field) => !this.validateLeadField(field));
    if (invalid.length > 0) {
      error.hidden = true; // за поле говорит его собственная строка ошибки
      invalid[0].focus();
      return;
    }
    error.hidden = true;
    try {
      await this.api.postLead({
        name: name.value.trim(),
        phone: phone.value.trim(),
        email: email.value.trim(),
        visitor_key: this.visitorKey,
        page_path: window.location.pathname,
        // Форму заполняют и те, кто с ботом не говорил, — тогда сервер ClientID
        // из разговора не возьмёт, и это единственный источник ключа. Номер
        // счётчика по той же причине: сообщений не было, запомнить его негде.
        metrika_client_id: this.metrikaClientId,
        metrika_counter_id: this.metrikaCounterId,
      });
      this.leadSubmitted = true;
      this.markLeadDone(); // переживает reload: без метки revive поднял бы форму снова
      form.remove();
      this.leadForm = null;
      this.toggleContinueChips(true);
      this.appendBubble('local', this.t('lead_success'));
      // Контакт с оффтопа целью не считаем: заявка из нецелевого разговора не
      // стоит той же ставки, что заявка по услуге (§0).
      if (this.botAnsweredOnTopic) this.emitTrack('lead');
    } catch (err) {
      error.textContent =
        err instanceof ApiError ? err.message || this.t('error_generic') : this.t('offline');
      error.hidden = false;
    }
  }

  /** Контакт уже оставлен: in-memory флаг за эту загрузку ИЛИ метка localStorage
   *  из прошлого визита. Заявка (POST /leads) сообщения в историю не пишет,
   *  поэтому после reload последним словом остаётся просьба контакта — без метки
   *  reviveContactAsk поднял бы форму снова и напросился на дубль (Codex P2). */
  private leadAlreadySubmitted(): boolean {
    if (this.leadSubmitted) return true;
    try {
      return window.localStorage.getItem(this.leadDoneKey()) === '1';
    } catch {
      return false;
    }
  }

  private markLeadDone(): void {
    try {
      window.localStorage.setItem(this.leadDoneKey(), '1');
    } catch {
      // приватный режим — метка живёт до перезагрузки (in-memory leadSubmitted)
    }
  }

  /** Ключ per-посетитель (visitor_key = личность его site-диалога), не per-widget:
   *  «этот гость оставил контакт», а не «на этом сайте кто-то оставлял». */
  private leadDoneKey(): string {
    return `assiistent_lead_done_${this.visitorKey}`;
  }

  /** Ре-показ формы после перезагрузки (порт reviveContactAsk marketpult): если
   *  последнее слово бота — свежая просьба контакта (message.lead), поднимаем
   *  форму снова, чтобы ушедший на полпути посетитель продолжил. */
  private reviveContactAsk(messages: ChatMessage[]): void {
    if (this.leadAlreadySubmitted() || this.leadForm?.isConnected) return;
    const last = messages[messages.length - 1];
    if (!last?.lead) return;
    if (Date.now() - new Date(last.created_at).getTime() > CONTACT_REVIVE_MS) return;
    this.openLeadForm(this.lastLeadCta ?? undefined);
  }

  // --- Утилиты ---------------------------------------------------------------

  private t(key: string): string {
    return this.config?.texts[key] ?? FALLBACK_TEXTS[key] ?? key;
  }

  private el<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    className: string,
    text?: string,
  ): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
}
